import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { DeleteOutlined, ShareAltOutlined } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Chip } from '../../components/ui/Chip'
import { Icon } from '../../components/ui/Icon'
import { Spinner } from '../../components/ui/Spinner'
import { useToast } from '../../components/ui/ToastProvider'
import { RowActionsMenuPortal, useRowActionsMenu } from '../../components/ui/rowActionsMenu'
import { apiClient } from '../../lib/api/client'
import { useSessionWithoutProvider } from '../../lib/auth/use-session'
import { displayNameFor, findSourceableById } from '../../lib/fixtures/sourceable-lookup'
import { useRangeSelection } from '../../lib/list/useRangeSelection'
import { toAddCartLineRequest } from '../cart/cart-catalog-lookup'
import { addAmounts, formatMoney, multiplyAmount } from '../cart/cart-money'
import { AddPartsToRfqModal } from './AddPartsToRfqModal'
import { MoveToNewRfqConfirmModal } from './MoveToNewRfqConfirmModal'
import { QuoteVariantSelector } from './QuoteVariantSelector'
import { RfqEtaChip } from './RfqEtaChip'
import { RfqLineConfiguratorModal } from './RfqLineConfiguratorModal'
import { RfqLineFileChip, drawingFilesOnly } from './RfqLineFileChip'
import type { RfqPartsCardRow } from './RfqPartsCard'
import { RfqPartsCard } from './RfqPartsCard'
import { StatusStepper } from './StatusStepper'
import { searchAddPartsCandidates } from './add-parts-search'
import type { RfqLineSpecification } from './configuration-estimate'
import type {
  Quantity,
  Quote,
  QuoteVariant,
  Rfq,
  RfqLineFileView,
  RfqLineItem,
  RfqLinePartView,
  RfqLineView,
  SharedSession,
} from './domain'
import {
  RFQ_BUNDLED_NOTE,
  etaForRfq,
  formatQuantity,
  isQuoteLineAddable,
  isSharedSessionExpired,
  materialLabelForLine,
  partCaption,
  resolveQuoteVariantId,
  showsInProgressStepper,
} from './domain'
import { buildAppendLinesRequest } from './review-domain'
import type { RfqReviewLine } from './review-domain'
import {
  ADD_PARTS_QUOTE_IN_PROGRESS_COPY,
  type AppendLinesConflict,
  classifyAppendConflict,
} from './rfq-append-lines'
import { previewDiscountedUnitPrice } from './rfq-volume-break-preview'
// UID-135 (ADR-0056 Decision 2/4): `useCardQuote` moved verbatim to
// `use-rfq-quote.ts` (renamed `useRfqQuote`) so the new `RfqListRow` and
// `RfqDetailSummaryRail` can share the exact same fetch — this is the ONLY
// edit UID-135 makes to this file; every other line, including the 52
// existing tests' expectations, is unchanged.
import { useRfqQuote } from './use-rfq-quote'
import type { QuoteLoadState } from './use-rfq-quote'
import { applyVariantRatio, deliveryOptionForVariant } from './variant-pricing'

/** Follow-up to UID-265b — a newly-appended line (`appendPart` above) lands
 * on the server with `specSelection: null`, exactly like a freshly-added
 * compose-flow line (`toReviewLine`, `review-domain.ts`). Before this fix,
 * `RfqCard`'s non-quoted parts table had no Configure affordance at all, so
 * a buyer who added a part to an already-submitted RFQ had no way to set
 * its material/spec/quantity.
 *
 * Mirrors `review-domain.ts`'s `needsConfigure` predicate exactly (a catalog
 * line never needs Configure; any other line needs it until it carries a
 * real `specification`), just typed over this card's own `RfqLineView`
 * shape instead of the compose flow's local `RfqReviewLine` — the two
 * schemas agree on both fields this reads (`sourceable.kind`/
 * `specSelection.specification`), so there is no risk of the two predicates
 * disagreeing on the same server data. */
function lineNeedsConfigure(line: RfqLineView): boolean {
  return line.sourceable.kind !== 'catalog' && !line.specSelection?.specification
}

/** Adapts one of THIS card's own `RfqLineView` server lines into the
 * `RfqReviewLine` shape `RfqLineConfiguratorModal` (and its
 * `fromRfqReviewLine` subject adapter) expect — the compose flow's local
 * draft-line shape. This card has no compose-flow draft to read an existing
 * `RfqReviewLine` from (it operates directly on an already-submitted RFQ's
 * lines), so this is the one, minimal mapping down to exactly the fields
 * the modal's save path reads: `id`/`name`/`sku`/`quantity`/`specSelection`,
 * plus the sourceable's `imageRef` preview field (from `line.part.thumbnailRef`,
 * this card's own image source — see `nonQuotedPartsRows`'s `thumbnailRef`
 * above) so the shell's Configuration-tab preview isn't blank. `indicativePrice`/
 * `pricingProjection` have no counterpart on a submitted RFQ's `SourceableRef`
 * (unlike a freshly-fetched `Sourceable` the compose flow's `AddPartsToRfqModal`
 * holds), so they are simply omitted, never fabricated. */
function toRfqReviewLineFromView(line: RfqLineView): RfqReviewLine {
  return {
    id: line.sourceable.id,
    sourceable: {
      ...line.sourceable,
      ...(line.part?.thumbnailRef ? { imageRef: line.part.thumbnailRef } : {}),
    },
    name: line.part?.name ?? displayNameFor(line.sourceable),
    sku: line.part?.partNumber ?? null,
    quantity: line.quantity.value,
    specSelection: line.specSelection ?? null,
  }
}

export interface RfqCardProps {
  readonly rfq: Rfq
  /** Re-fetches the RFQ list — called after an action that changes this
   * card's status (accept-and-add-to-cart, re-request). UID-611 (ADR-0178
   * D2/ADR-0029) widened this to optionally carry the FRESH, already-derived
   * `RfqView` a quantity-PATCH/line-removal response returns — passing it
   * lets the caller replace its view state WHOLESALE from the server's
   * response in one round trip instead of paying for a second `GET`, the
   * same pattern `RfqDetailSummaryRail`'s own `onChanged` already uses for
   * append/resubmit. Called with no argument for every other existing
   * action (accept-and-add-to-cart, re-request), which still means "refetch
   * from scratch," unchanged. */
  readonly onChanged: (fresh?: Rfq) => void
  /** UID-611 (ADR-0178 D3) — fired instead of `onChanged` when a line removal
   * (single or batch) took the RFQ's LAST remaining line and the RFQ itself
   * was cascade-deleted server-side. The caller must navigate away from
   * `/rfq/:rfqId` rather than attempt to re-render a now-deleted resource —
   * this card holds no route knowledge of its own. Optional so this card's
   * router-less test suite keeps working (a cascade delete there simply has
   * nowhere to navigate, which is fine — the test asserts the DELETE call,
   * not the navigation). */
  readonly onRfqDeleted?: () => void
  /** UID-238 item 2 (ADR-0056 amendment) — the pricing-region variant
   * currently selected for this RFQ's quote, LIFTED to `RfqDetailPage` so it
   * can be shared with the co-mounted `RfqDetailSummaryRail` (closes the
   * ADR-0056 Decision 2/D1 gap where the two surfaces couldn't stay in
   * sync). `undefined` when nothing has been explicitly picked yet — every
   * consumer resolves that to the first variant itself, never a value
   * invented here. Both props are OPTIONAL: `RfqCard`'s own test suite
   * mounts it standalone (no lifted parent state), so an internal
   * `useState` fallback (below) keeps that "uncontrolled" usage working
   * byte-for-byte as before — this is the same controlled/uncontrolled
   * pattern as a native `<input>`. */
  readonly selectedVariantId?: QuoteVariant['id'] | undefined
  readonly onVariantSelect?: ((id: QuoteVariant['id']) => void) | undefined
  /** UID-294 — the "Add parts to this RFQ" modal's "Upload new files" footer
   * action hands off to the CAD-intake screen. Optional so RfqCard's
   * router-less test suite keeps working (defaults to a demo toast). */
  readonly onUploadNewFiles?: (() => void) | undefined
  /** UID-586 — the Create-RFQ compose flow's in-page submission banner
   * (`RfqSubmissionStatusBanner`), passed through verbatim to whichever
   * `RfqPartsCard` instance is currently mounted (non-quoted or, via
   * `QuotedLinesPanel`, quoted) — the exact slot the design puts it in,
   * between that card's header bar and its table. `undefined` for every
   * OTHER caller: `RfqDetailPage` never passes this, so `RfqCard` renders
   * byte-for-byte as before there. */
  readonly submissionBanner?: ReactNode
}

/**
 * One RFQ card (screens.md §14): status pill, the Submitted→Sourcing→Quoted
 * stepper, the request's parts, and — only once a Quote exists — the
 * quoted-lines picker with "Add to cart." `quotingSubState`/`takeoverReason`
 * (UID-52/53's automated-vs-CPQ split) are CPQ/ops-internal and are never
 * fetched or rendered here (`domain.ts`'s `tabForStatus`/`RFQ_STATUS_CHIP`
 * doc comments record the same decision) — this card only ever reflects the
 * coarse, customer-facing `RfqStatus`.
 */
// Perf (no behavior change) — wrapped in `React.memo` so a parent re-render
// (e.g. `RfqDetailPage`'s own state churn) doesn't force this card to
// re-derive its per-line maps/rows below when its own props haven't
// changed. Same `memo`-wrapped-named-export pattern already used for
// `PartsReviewTable.tsx`'s sub-components — reused here for consistency.
// Only pays off if callers pass stable prop identities (see `onChanged`/
// `onVariantSelect` — `RfqDetailPage` wraps those in `useCallback`).
export const RfqCard = memo(function RfqCard({
  rfq,
  onChanged,
  onRfqDeleted,
  selectedVariantId: controlledSelectedVariantId,
  onVariantSelect: controlledOnVariantSelect,
  onUploadNewFiles,
  submissionBanner,
}: RfqCardProps) {
  // UID-325 (Gate 5) — the `RFQ_STATUS_CHIP[rfq.status]` lookup that used to
  // live here is gone with the status chip itself (AC5 moved it to
  // `RfqDetailPage`'s heading row, which does its own lookup). Left as a note
  // rather than silently dropped, since `RfqListRow` still reads the same map.
  const handoff = useShareHandoff(rfq.id, rfq.status === 'accepted')
  // UID-289 — the "Add parts" modal's demo-honest toast (see its render
  // below); `useShareHandoff` has its OWN internal `useToast()` for the
  // mark-ready flow, but that instance isn't reachable here.
  const { showToast } = useToast()

  // UID-238 item 2 — the controlled/uncontrolled fallback described on
  // `RfqCardProps` above: when a parent doesn't lift the selection (every
  // existing `RfqCard.test.tsx` case), this internal state is the ONLY
  // store and behaves exactly as the old `QuotedLinesPanel`-local `useState`
  // did. When a parent DOES lift it (the real `RfqDetailPage` mount site),
  // `controlledSelectedVariantId` always wins once it's set.
  const [internalSelectedVariantId, setInternalSelectedVariantId] = useState<
    QuoteVariant['id'] | undefined
  >(undefined)
  // UID-238 item 2 (Gate 5 review — PR #202, Manjeet): reset this internal
  // fallback whenever the RFQ this card represents changes, so a pricing-
  // region pick made for one RFQ never bleeds into another. `RfqDetailPage`
  // already resets its OWN lifted copy on `:rfqId` change
  // (`useEffect(() => setSelectedVariantId(undefined), [rfqId])`), but that
  // reset can't reach this child-local copy — and while the detail page's
  // loading-gate happens to remount this card on an `:rfqId`-only route
  // change today (incidentally clearing this state), relying on that is
  // fragile: any mount site that re-renders `RfqCard` with a new `rfq`
  // WITHOUT a remount (the standard React Router "same element, new params"
  // case) would otherwise leak the prior RFQ's pick through the controlled/
  // uncontrolled `?? internalSelectedVariantId` fallback into the picker,
  // rail, and — via `effectiveLine` — what Checkout actually POSTs. This
  // mirrors the parent's reset pattern exactly, scoped to `rfq.id` (a
  // same-`id` re-render, e.g. an `onChanged()` refetch, deliberately does
  // NOT reset).
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — reset WHEN rfq.id changes; the body doesn't read it (stable setter), same rationale as RfqDetailPage's own reset effect.
  useEffect(() => {
    setInternalSelectedVariantId(undefined)
  }, [rfq.id])
  const effectiveSelectedVariantId = controlledSelectedVariantId ?? internalSelectedVariantId
  function handleVariantSelect(id: QuoteVariant['id']) {
    setInternalSelectedVariantId(id)
    controlledOnVariantSelect?.(id)
  }

  // UID-134: the Quote is fetched ONCE at the card level for a quoted/accepted
  // RFQ and shared by both the row summary (ship-by + best-option) and the
  // QuotedLinesPanel below — the panel used to own this fetch; lifting it here
  // keeps the two quote-derived list fields on ONE fetch (Gate 3: "reuse the
  // quote already fetched"), never a second price/date contract surface.
  const showsQuote = (rfq.status === 'quoted' || rfq.status === 'accepted') && Boolean(rfq.quoteId)
  const { quote, quoteLoadState } = useRfqQuote(rfq.id, showsQuote)

  // UID-289 (ADR-0093 §C2/§C5): the per-line DISPLAY identity (name/
  // partNumber/process/thumbnailRef) lives on the RFQ's OWN lines — the
  // Quote's `LineItem` carries no such field, so the quoted-lines table
  // below looks this up by `sourceable.id` rather than reading it off the
  // Quote line. The `as readonly RfqLineView[]` cast is required, not
  // incidental: openapi-typescript infers `Rfq['lines']`'s element type as
  // an intersection of the frozen `Rfq.lines: LineItem[]` and the additive
  // `RfqView.lines?: RfqLineView[]`, which does NOT expose `part`/
  // `specSelection`/`files` through plain property access — see the
  // `RfqLineView` doc comment in `./domain` for the full explanation. Safe
  // here because the server always emits `RfqView` (never the bare frozen
  // `Rfq`) for every response this component reads.
  // Perf (no behavior change) — memoized on `rfq.lines`, the only input.
  const partBySourceableId = useMemo(
    () =>
      new Map<string, RfqLinePartView | undefined>(
        (rfq.lines as readonly RfqLineView[]).map((line) => [line.sourceable.id, line.part]),
      ),
    [rfq.lines],
  )

  // ADR-0131 — the EFFECTIVE per-line quantity is the RFQ REQUEST line's quantity (the one the
  // buyer set/edited on the RFQ, `rfq.lines`), NOT the Quote `LineItem`'s copy, which can lag the
  // edited request quantity. Keyed by sourceable id so the row add (`handleRowAdd`) posts the
  // quantity the RFQ screen actually shows, matching the parts table above (`quantity: line.quantity`).
  // Perf (no behavior change) — memoized on `rfq.lines`, the only input.
  const quantityBySourceableId = useMemo(
    () =>
      new Map(
        (rfq.lines as readonly RfqLineView[]).map((line) => [line.sourceable.id, line.quantity]),
      ),
    [rfq.lines],
  )

  // ADR-0115 D3 — the per-line Configure-step material selection, the
  // `specSelection` sibling of `partBySourceableId` above (same reason: the
  // Quote's own `LineItem` carries no `specSelection` field either). Feeds
  // the Requirements column's optional `· material` suffix via the ONE
  // named `materialLabelForLine` accessor — never read inline at either
  // caller's render site.
  // Perf (no behavior change) — memoized on `rfq.lines`, the only input.
  const specSelectionBySourceableId = useMemo(
    () =>
      new Map<string, RfqLineView['specSelection']>(
        (rfq.lines as readonly RfqLineView[]).map((line) => [
          line.sourceable.id,
          line.specSelection,
        ]),
      ),
    [rfq.lines],
  )

  // UID-327/328 (ADR-0112 D2) — the per-line supporting-file chips, threaded
  // through the SAME way as `partBySourceableId` above (the owning RFQ's
  // own lines, never the Quote's `LineItem`, which carries no `files` field
  // either). This is the one place the quoted path gains something the
  // pre-ADR-0112 `<ul>` used to show only for a non-quoted RFQ — a
  // disclosed, deliberate exception (ADR-0112 D2).
  // Perf (no behavior change) — memoized on `rfq.lines`, the only input.
  const filesBySourceableId = useMemo(
    () =>
      new Map<string, readonly RfqLineFileView[] | undefined>(
        (rfq.lines as readonly RfqLineView[]).map((line) => [line.sourceable.id, line.files]),
      ),
    [rfq.lines],
  )

  // UID-327/328 (ADR-0112 D1) — the non-quoted path's `RfqPartsCard` rows,
  // built straight from the RFQ's OWN lines (never a Quote — this path only
  // ever renders when `!showsQuote`). No checkbox/actions/unit-price —
  // `RfqPartsCard` itself only renders those when `isReturned`, so this
  // caller simply never supplies them.
  // Perf (no behavior change) — memoized on `rfq.lines` and `showToast`
  // (the latter is only read inside a row's `onDownloadError` closure;
  // `ToastProvider` keeps it `useCallback`-stable, so this doesn't defeat
  // the memo in practice).
  // UID-586 — the design's per-row `p.isPending` pill (`dc.html:4224`:
  // spinner + "Quote in progress", `.fx-spin` first, text second) in the
  // Unit price column, while awaiting the automated quote. Scoped to the
  // Create-RFQ compose flow's own transition ONLY — `submissionBanner` is
  // the same signal that scopes the banner itself, so this pill and that
  // banner appear and disappear together. Never shown on the RFQ detail
  // page/list (which never pass `submissionBanner`), where a `requested`/
  // `quoting` RFQ may simply be one the customer is revisiting, not one
  // just submitted in this session.
  const showQuoteInProgressPill =
    submissionBanner !== undefined && (rfq.status === 'requested' || rfq.status === 'quoting')

  // Follow-up to UID-265b — the per-line configure modal's target, keyed by
  // `sourceable.id` (the same id `appendPart`/`onConfigureLine` above use),
  // never a row index (unstable across re-sort/re-filter/re-render, ADR-0064
  // D1's same discipline). `null` when the modal is closed. Declared ABOVE
  // `nonQuotedPartsRows` below, which reads `setConfigureLineId` from its
  // per-row Configure button.
  const [configureLineId, setConfigureLineId] = useState<string | null>(null)

  // Follow-up to UID-265b (real contract constraint, not a style choice) —
  // `POST /rfq/{rfqId}/lines/bulk-configure`'s OWN service (`apps/api`'s
  // `bulkConfigureLines`) refuses any RFQ whose status is not `draft`
  // (`RfqConflict 'lines-not-editable'`) — this card, by construction, only
  // ever mounts for a SUBMITTED (non-draft) RFQ. A `requested`/`quoting` RFQ
  // is the one case a buyer could plausibly expect to still edit (it hasn't
  // been quoted yet, and appending a part — `appendPart` above — is already
  // possible in exactly these two statuses); `quoted`/`accepted`/`declined`/
  // `expired` are terminal reads, matching the SAME invariant ADR-0112 D1
  // already established for this card's other addable-only affordances
  // (bulk-select, add-to-cart, row menu — all absent outside their gated
  // states). Scoping the button to these two statuses keeps that invariant
  // intact rather than offering an action every save of which 409s.
  const canConfigureNonQuotedLines = rfq.status === 'requested' || rfq.status === 'quoting'

  // UID-611 (ADR-0178 D1/D5) — per-line busy trackers for the non-quoted
  // table's real quantity-PATCH and DELETE, keyed on `sourceable.id` (the
  // same key convention every other per-line map on this card uses). Each
  // in-flight request disables its own row's control so a second click
  // can't race the first; neither status is ever gated on `rfq.status` —
  // both are permitted on every status by design (ADR-0178 D1).
  const [qtyBusyIds, setQtyBusyIds] = useState<ReadonlySet<string>>(new Set())
  const [deleteBusyIds, setDeleteBusyIds] = useState<ReadonlySet<string>>(new Set())

  /** UID-611 (ADR-0178 D1/D2) — commits a quantity change for one non-quoted
   * line via `PATCH /rfq/{rfqId}/lines/{lineId}`. The stored per-unit price
   * is never touched by this request (the server carries it through
   * unchanged); this handler replaces the WHOLE RFQ view from the server's
   * response (`onChanged(data)`) rather than recomputing any subtotal/total
   * itself (ADR-0029, reused verbatim by ADR-0178 D2).
   *
   * Review-round fixes (UID-611):
   *  - Resolves `boolean` (was `void`) so `RfqLineQtyInput` can roll its own
   *    optimistic display back to the last-committed value on a failure —
   *    see that component's `commit()` doc comment.
   *  - Wrapped in a real `try`/`catch` (not just `try`/`finally`): a THROWN
   *    fetch error (offline, DNS failure — `openapi-fetch` rethrows when
   *    `fetch` itself rejects, same failure mode `useShareHandoff.markReady`
   *    already had to guard against, UID-438 Gate-5 CRITICAL 1) used to
   *    escape uncaught here, surfacing NO toast and leaving the customer
   *    with no idea the edit failed. */
  const handleLineQuantityChange = useCallback(
    async (lineId: string, uom: Quantity['uom'], nextValue: number): Promise<boolean> => {
      setQtyBusyIds((current) => new Set([...current, lineId]))
      try {
        const { data, error } = await apiClient.PATCH('/rfq/{rfqId}/lines/{lineId}', {
          params: { path: { rfqId: rfq.id, lineId } },
          body: { quantity: { value: nextValue, uom } },
        })
        if (data) {
          onChanged(data)
          return true
        }
        showToast(error?.detail ?? 'Could not update quantity. Please try again.', 'error')
        return false
      } catch {
        showToast('Could not update quantity. Check your connection and try again.', 'error')
        return false
      } finally {
        setQtyBusyIds((current) => {
          const next = new Set(current)
          next.delete(lineId)
          return next
        })
      }
    },
    [rfq.id, onChanged, showToast],
  )

  // UID-611 review round (WCAG 2.4.3/4.1.3) — the non-quoted table's own
  // per-line-remove focus/announcement pair, the twin of `QuotedLinesPanel`'s
  // pre-existing `liveMessage`. A removed row's kebab trigger is unmounted
  // WITH the row, so without this the browser drops focus to `<body>` (a
  // real regression, not a style nit — the next Tab press would restart from
  // the top of the document). `addPartsBtnRef` (wired to `RfqPartsCard`
  // below) is a STABLE anchor that survives every removal (it never
  // disappears while this card renders at all), so it is where focus lands;
  // `lineLiveMessage` is the `aria-live="polite"` announcement of what
  // happened, read by assistive tech even though sighted focus already moved.
  const addPartsBtnRef = useRef<HTMLButtonElement | null>(null)
  const [lineLiveMessage, setLineLiveMessage] = useState('')
  const pendingFocusRestoreRef = useRef(false)
  // Runs AFTER the row that was removed is actually gone from the DOM (this
  // effect is keyed on `rfq.lines`, which only changes once `onChanged`'s
  // fresh `RfqView` has been committed to state and re-rendered) — focusing
  // any earlier would target a node about to be removed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — re-run WHEN rfq.lines changes; the body reads `pendingFocusRestoreRef`/`addPartsBtnRef` (refs, not reactive deps).
  useEffect(() => {
    if (!pendingFocusRestoreRef.current) return
    pendingFocusRestoreRef.current = false
    addPartsBtnRef.current?.focus()
  }, [rfq.lines])

  /** UID-611 (ADR-0178 D1/D3, ADR-0179 D1) — removes one non-quoted line via
   * the real `DELETE /rfq/{rfqId}/lines/{lineId}`, superseding the demo
   * toast the kebab used to fire. Branches on the ADR-0178 D3 cascade: if
   * this removal took the RFQ's last line, the RFQ itself was deleted
   * server-side, and the caller must navigate away (`onRfqDeleted`) rather
   * than render a now-stale view.
   *
   * Review-round fix (UID-611) — real `try`/`catch`, same reasoning as
   * `handleLineQuantityChange` above: a thrown fetch failure used to escape
   * uncaught, silently, with no toast. */
  const handleLineRemove = useCallback(
    async (lineId: string, partName: string) => {
      setDeleteBusyIds((current) => new Set([...current, lineId]))
      try {
        const { data, error } = await apiClient.DELETE('/rfq/{rfqId}/lines/{lineId}', {
          params: { path: { rfqId: rfq.id, lineId } },
        })
        if (data) {
          if (data.rfqDeleted) {
            onRfqDeleted?.()
            return
          }
          if (data.rfq) {
            onChanged(data.rfq)
          }
          showToast(`Removed “${partName}” from this request.`, 'success')
          setLineLiveMessage(`Removed ${partName} from this request.`)
          pendingFocusRestoreRef.current = true
        } else {
          showToast(error?.detail ?? `Couldn't remove “${partName}”. Please try again.`, 'error')
        }
      } catch {
        showToast(`Couldn't remove “${partName}”. Check your connection and try again.`, 'error')
      } finally {
        setDeleteBusyIds((current) => {
          const next = new Set(current)
          next.delete(lineId)
          return next
        })
      }
    },
    [rfq.id, onChanged, onRfqDeleted, showToast],
  )

  const nonQuotedPartsRows: readonly RfqPartsCardRow[] = useMemo(
    () =>
      (rfq.lines as readonly RfqLineView[]).map((line, index) => ({
        key: `${line.sourceable.id}-${index}`,
        thumbnailRef: line.part?.thumbnailRef ?? null,
        name: line.part?.name ?? displayNameFor(line.sourceable),
        caption: partCaption(line.part),
        fileChips: (() => {
          const drawingFiles = drawingFilesOnly(line.files)
          return drawingFiles && drawingFiles.length > 0 ? (
            <div className="rfq-card-line-files">
              {drawingFiles.map((file, fileIndex) => (
                <RfqLineFileChip
                  key={file.id ?? `${line.sourceable.id}-file-${fileIndex}`}
                  partId={line.sourceable.id}
                  file={file}
                  onDownloadError={(message) => showToast(message, 'error')}
                />
              ))}
            </div>
          ) : undefined
        })(),
        requirementsCell: (
          <RequirementsCell
            process={line.part?.process ?? null}
            material={materialLabelForLine(line.specSelection)}
          />
        ),
        quantity: line.quantity,
        // UID-611 (ADR-0178 D1/D5) — the real, persisted quantity input, on
        // this non-quoted table exactly as on the quoted one (no status
        // gating). Review-round fix — returns the handler's own
        // `Promise<boolean>` (not `void`-wrapped) so `RfqLineQtyInput` can
        // roll back its optimistic display on a failed PATCH.
        onQuantityChange: (next: number) =>
          handleLineQuantityChange(line.sourceable.id, line.quantity.uom, next),
        quantityBusy: qtyBusyIds.has(line.sourceable.id),
        unitPriceLabel: showQuoteInProgressPill ? <QuoteInProgressPill /> : undefined,
        // Follow-up to UID-265b — the Configure affordance a newly-appended
        // (or any other still-unconfigured) non-quoted line needs, alongside
        // the UID-611 (ADR-0178 D5) kebab menu — which now renders on EVERY
        // row, not just quoted ones — wired to the real `DELETE`.
        actions: (
          <>
            {canConfigureNonQuotedLines && lineNeedsConfigure(line) && (
              <ConfigureLineButton
                lineId={line.sourceable.id}
                onConfigureLine={setConfigureLineId}
              />
            )}
            <MoreActionsButton
              partName={line.part?.name ?? displayNameFor(line.sourceable)}
              onRemove={() =>
                void handleLineRemove(
                  line.sourceable.id,
                  line.part?.name ?? displayNameFor(line.sourceable),
                )
              }
              busy={deleteBusyIds.has(line.sourceable.id)}
            />
          </>
        ),
      })),
    [
      rfq.lines,
      showToast,
      showQuoteInProgressPill,
      canConfigureNonQuotedLines,
      qtyBusyIds,
      deleteBusyIds,
      handleLineQuantityChange,
      handleLineRemove,
    ],
  )

  const configureTargetLine = useMemo(
    () =>
      configureLineId
        ? (rfq.lines as readonly RfqLineView[]).find(
            (line) => line.sourceable.id === configureLineId,
          )
        : undefined,
    [rfq.lines, configureLineId],
  )
  const configureReviewLine = useMemo(
    () => (configureTargetLine ? toRfqReviewLineFromView(configureTargetLine) : undefined),
    [configureTargetLine],
  )

  /** Follow-up to UID-265b — the SAME `POST /rfq/{rfqId}/lines/bulk-configure`
   * seam `RfqReviewPage.handleSaveConfigure` targets, called directly against
   * this already-submitted RFQ's OWN `rfq.id` (mirroring `appendPart` above's
   * direct-call style) — never through `ensureLinesOnServerDraft`, which is a
   * compose-flow-only reconciliation step this card has no draft to run
   * against. `onChanged()` re-fetches the RFQ so the saved `specSelection`
   * (and the Configure button it gates) reflect the server's answer, the
   * same refetch-not-optimistic-merge pattern `appendPart` already uses. */
  async function handleSaveLineConfigure(
    lineId: string,
    specification: RfqLineSpecification,
    quantity: number,
  ): Promise<{ readonly ok: true } | { readonly ok: false; readonly message: string }> {
    const { data, error } = await apiClient.POST('/rfq/{rfqId}/lines/bulk-configure', {
      params: { path: { rfqId: rfq.id } },
      body: { lineIds: [lineId], specification, quantity },
    })
    if (!data) {
      return {
        ok: false,
        message: error?.detail ?? 'Could not save this configuration. Please try again.',
      }
    }
    onChanged()
    return { ok: true }
  }

  const [addPartsOpen, setAddPartsOpen] = useState(false)
  // Perf (no behavior change) — memoized on `rfq.lines`, the only input.
  const alreadyAddedSourceableIds = useMemo(
    () => new Set(rfq.lines.map((line) => line.sourceable.id)),
    [rfq.lines],
  )

  // UID-265b (ADR-0093) — the REAL "Add parts to this RFQ" append, wired the
  // same way `RfqDetailSummaryRail.tsx`'s `ActiveSummaryRail.append`/
  // `handleAdd` already do (that surface's append is exercised and tested;
  // this is the exact same conflict classification, toast copy, and
  // double-submit guard, inlined here rather than extracted into a shared
  // hook — a pre-demo bugfix on this heavily-annotated file is safer done as
  // a direct, verifiable port than as a cross-file refactor of an
  // already-working surface). Supersedes the demo-honest stub toast this
  // modal's `onAdd` used to fire.
  //
  // `useSessionWithoutProvider` (not the ordinary `useSession`), same
  // rationale as that hook's own doc comment (`use-session.ts`): `RfqCard`'s
  // own test suite mounts this component directly under `ToastProvider`
  // only, with no `<AuthProvider>` ancestor (unlike the real app, which
  // always renders it behind `RequireAuth`) — `useSession` would throw there.
  const session = useSessionWithoutProvider()
  const [addingPart, setAddingPart] = useState(false)
  const addingPartRef = useRef(false)
  /** Guards the single `stale` retry (ADR-0093 D1's 409 (4)) — one retry per
   * user-initiated add, reset at the start of each attempt and on every
   * open/close of the dialog (mirrors `ActiveSummaryRail`'s own guard). */
  const retriedStalePartRef = useRef(false)

  function setAddPartsDialog(open: boolean) {
    retriedStalePartRef.current = false
    setAddPartsOpen(open)
  }

  async function appendPart(sourceable: Parameters<typeof buildAppendLinesRequest>[0][number]) {
    if (!session) return
    // Send ONLY the part being added — never the RFQ's existing set (same
    // ADR-0093 D2/F7 contract `ActiveSummaryRail.append` follows).
    const body = buildAppendLinesRequest([sourceable], session.accountContext.accountId)
    const { data, error } = await apiClient.POST('/rfq/{rfqId}/lines', {
      params: { path: { rfqId: rfq.id } },
      body,
    })
    if (data) {
      // The 200 means "the part is on the RFQ and re-pricing has been
      // kicked," never "repriced" (ADR-0093 D3) — same copy as
      // `ActiveSummaryRail`. `onChanged()` (no argument) drives the existing
      // `RfqDetailPage` refetch path rather than a second, ad-hoc GET here.
      showToast('Part added — we’re re-pricing this RFQ.', 'success')
      onChanged()
      return
    }
    const conflict: AppendLinesConflict =
      (error as { status?: number } | undefined)?.status === 409
        ? classifyAppendConflict(error?.detail)
        : 'unknown'
    switch (conflict) {
      case 'line-already-present':
        showToast(error?.detail ?? 'That part is already on this RFQ.', 'error')
        onChanged()
        return
      case 'quote-in-progress':
        showToast(ADD_PARTS_QUOTE_IN_PROGRESS_COPY, 'error')
        setAddPartsDialog(false)
        onChanged()
        return
      case 'lines-not-appendable':
        showToast(error?.detail ?? 'Parts can no longer be added to this RFQ.', 'error')
        setAddPartsDialog(false)
        onChanged()
        return
      case 'stale': {
        // Concurrent write; the append's compare-and-set lost. Retry ONCE.
        if (retriedStalePartRef.current) {
          showToast(error?.detail ?? 'This RFQ changed while we were adding that part.', 'error')
          onChanged()
          return
        }
        retriedStalePartRef.current = true
        await appendPart(sourceable)
        return
      }
      case 'line-not-configured':
        // Defense-in-depth 409 — the modal's own `kind === 'configurable'`
        // gate should make this unreachable from the UI, so this is a rare
        // race/stale-client case, not an RFQ-wide state change: leave the
        // dialog open (unlike the two RFQ-wide branches above) so the
        // customer can keep adding other parts, and skip `onChanged()` since
        // nothing on the server actually changed.
        showToast(
          error?.detail ??
            'This part is a configurable catalog series and must be configured before it can be added to an RFQ.',
          'error',
        )
        return
      default:
        showToast(error?.detail ?? 'Could not add that part. Please try again.', 'error')
        onChanged()
        return
    }
  }

  async function handleAddPart(sourceable: Parameters<typeof appendPart>[0]) {
    if (addingPartRef.current) return
    addingPartRef.current = true
    retriedStalePartRef.current = false
    setAddingPart(true)
    try {
      await appendPart(sourceable)
    } finally {
      addingPartRef.current = false
      setAddingPart(false)
    }
  }

  // UID-501 — the requested/quoting in-progress gate, computed once here
  // (rather than re-checked at both render sites below) so the banner card's
  // ETA gate and the stepper card agree on the exact same condition. The
  // parts card needs no such gate: its 16px offset comes from an
  // adjacent-sibling selector in `rfq.css`, so there is no spacing modifier
  // to keep in sync here (see ADR-0158 D1).
  // UID-586 — suppressed whenever `submissionBanner` is present: the
  // Create-RFQ compose flow's own banner (rendered inside the parts card
  // below, via that same prop) already carries this exact "Submitted ->
  // Sourcing -> Quoted" progress — showing both was a real, visible
  // duplicate (confirmed against a screenshot of the actual build). Never
  // suppressed on the RFQ detail page/list, which never pass that prop and
  // have no other progress indicator of their own.
  const inProgress = showsInProgressStepper(rfq.status) && submissionBanner === undefined
  // Gate-5 M2 (ADR-0093 D5) — the estimate is anchored on the SUBMISSION,
  // preferring `submittedAt` over `createdAt`. Post-ADR-0082 eager-draft-
  // creation `createdAt` is the moment the first part was added at compose
  // time, potentially days before the RFQ entered the funnel — and this
  // value also feeds the detail rail's "Submitted <submittedAt>" line, so a
  // `createdAt`-anchored chip put two adjacent displays of the same event
  // openly in disagreement.
  //
  // The `?? createdAt` fallback is NOT the fallback D5 (and migration 2006's
  // header) forbid: that prohibition is on DISPLAYING a submission DATE
  // derived from `createdAt`, which would reassert the exact falsehood
  // UID-265c removed. This chip displays no submission date at all — only a
  // soft, non-binding, never-persisted estimated return (see `etaForRfq`'s
  // accepted-trade-off note). For a legacy row with a NULL `submittedAt`, a
  // degraded anchor is strictly better than dropping the estimate entirely,
  // and it asserts nothing about when the RFQ was submitted. With neither
  // field, `etaForRfq('')` returns null and the chip renders nothing,
  // unchanged.
  //
  // A follow-on that makes the anchor unambiguous would be a
  // `submittedAt`-only basis once every demo row carries the column (no
  // ticket filed — human direction).
  const inProgressEta = inProgress ? etaForRfq(rfq.submittedAt ?? rfq.createdAt ?? '') : null

  return (
    /* UID-485 b2 — a plain <div>, deliberately NOT the shared `Card` primitive.
     * The design's focused detail view has no outer card around the body: the
     * left column is a bare `flex-direction:column; gap:16px` stack of sibling
     * cards (`dc.html:3518-3519`), each of which (`.quote-options-card`,
     * `.rfq-parts-card`, `RfqDetailServicesCard`) carries its own chrome. Going
     * through `Card` prepended `.card`, whose white fill + 4px radius +
     * `--border-inset-1` drew a second, undesigned box AROUND those siblings.
     * The `.rfq-card` class stays (its own rules below still apply); only the
     * `.card` base is dropped. */
    // UID-586 — `rfq-card--compose-transition` scopes visual-only tweaks
    // (rfq.css) to the Create-RFQ compose flow's own post-submit render —
    // the SAME `submissionBanner`-presence signal that scopes the banner
    // and the "Quote in progress" pill. Never present on the RFQ detail
    // page/list, so nothing under this modifier can touch that surface.
    <div
      className={`rfq-card rfq-card-${rfq.status}${
        submissionBanner !== undefined ? ' rfq-card--compose-transition' : ''
      }`}
    >
      {/* UID-325 (AC5) — the STATUS chip moved up to `RfqDetailPage`'s heading
       * row, where the design puts it: inline, right of the RFQ number
       * (`dc.html:3511`). Rendering it here put it on its own row BELOW the
       * subheading. Everything else in this header stays: the UID-54 handoff
       * sub-state pair (no counterpart in the design's heading row, and
       * dependent on this component's own `useShareHandoff` call), the PO
       * reference, and the Bundled badge — the design does carry Bundled in
       * its heading row (`dc.html:3512`), but AC5 asks only for the status
       * badge, and this screen already surfaces bundling through the
       * separately-mounted `RfqBundledCallout` (a UID-294 placement
       * decision). Left as an observed, unactioned delta rather than widened
       * into a second badge migration. */}
      {/* UID-586 — gated on having anything to show at all. `.rfq-card-header`
       * (rfq.css) carries an unconditional `margin-bottom`, which used to be
       * invisible here (this card always sat below other content on the
       * detail page) but became a visible phantom gap once this card could
       * be the very FIRST thing in a column (the compose page, once
       * submitted) with nothing above it to absorb that margin. Omitting
       * the div entirely when empty removes the margin too — a pure
       * improvement on the detail page as well, not a behavior change. */}
      {(handoff.subState === 'ready' ||
        handoff.subState === 'expired' ||
        rfq.bundled === true ||
        rfq.poReference) && (
        <div className="rfq-card-header">
          <div className="rfq-card-header-badges">
            {handoff.subState === 'ready' && (
              <Chip className="rfq-chip" tone="info">
                Ready to share
              </Chip>
            )}
            {handoff.subState === 'expired' && (
              <Chip className="rfq-chip" tone="warning">
                Share link expired
              </Chip>
            )}
            {rfq.bundled === true && <BundledBadge />}
          </div>
          {rfq.poReference && <span className="rfq-card-po">PO {rfq.poReference}</span>}
        </div>
      )}

      {/* UID-325 (AC6) — quote options come FIRST in the design's detail body
       * (`dc.html:3520`, before the parts card at `:3559`); the build had them
       * inside `QuotedLinesPanel`, i.e. after this whole summary card. Mounted
       * here rather than reordering the panel internals so the parts heading
       * and the table below it stay in one component. Renders only once the
       * shared card-level quote fetch has resolved — no second fetch. */}
      {showsQuote && quote && quoteLoadState === 'loaded' && (
        <QuoteOptionsCard
          quote={quote}
          selectedVariantId={effectiveSelectedVariantId}
          onSelect={handleVariantSelect}
        />
      )}

      {/* UID-501 (closes ADR-0056 D1's descoped follow-on) — for the
       * requested/quoting body the design (`Fictiv Platform.dc.html`)
       * renders, in a parent `flex-direction:column; gap:16px` column
       * (`:3519`), the "Quote in progress" banner (`r.isActive`, sc-if
       * `:3535`, card div `:3536`), then the status tracker (`r.showTracker`,
       * sc-if `:3538`, card div `:3539`), then "Parts in this request"
       * (`r.showParts`, sc-if `:3559`, card div `:3560`) — THREE of the
       * design's FOUR sibling cards in this stack; the fourth,
       * `r.hasRequestInfo` ("YOUR REQUEST", `:3548-3557`), is deliberately
       * NOT implemented here (see the "Not done" note in ADR-0158 — its app
       * counterpart, `RequestDetailsSection.tsx`, mounts only on the compose
       * screen, never on this detail body, so nothing regresses; this is an
       * observed, unactioned delta, same idiom as the AC5 Bundled-badge note
       * above). This used to render `StatusStepper` THEN `RfqEtaChip` as bare
       * siblings with no card chrome of their own, AFTER the parts card,
       * inside the SAME `.rfq-card` — both the order and the chrome (one
       * merged card, not three) were wrong relative to the design. Fixed by
       * (1) moving this block ABOVE the parts card and swapping the internal
       * order so the banner leads, and (2) wrapping the banner/tracker each
       * in its own card div, copying the chrome literal (border/radius)
       * FROM `.rfq-parts-card` (below) — the sole existing precedent for it
       * in this file, not a pattern already shared by multiple cards; see
       * `rfq.css` for the two new classes
       * (`.rfq-progress-banner-card`/`.rfq-status-stepper-card`) and why
       * `.quote-options-card`/`.rfq-quoted-lines-panel` are NOT that
       * precedent. `StatusStepper`/`RfqEtaChip` internals (including the
       * PR #452 stepper-connector/eta-chip fixes) are untouched; only what
       * wraps them, and where that wrapper sits, changed. The inter-card
       * 16px gap is an adjacent-sibling CSS selector (`rfq.css`), not a
       * wrapper div or class — `.rfq-card` has no descendant/child/sibling
       * selectors anywhere in this file to justify one, and this component
       * needs none either now.
       *
       * The banner card is gated on `inProgressEta` (not just `inProgress`)
       * so it only renders when `RfqEtaChip` actually has something to show
       * — otherwise an empty bordered card would render with nothing
       * inside it (see `inProgressEta`'s own doc comment above). */}
      {inProgressEta && (
        <div className="rfq-progress-banner-card">
          <RfqEtaChip eta={inProgressEta} />
        </div>
      )}
      {inProgress && (
        <div className="rfq-status-stepper-card">
          <StatusStepper status={rfq.status} />
        </div>
      )}

      {/* UID-327/328 (ADR-0112 D1) — the parts card renders for EVERY
       * non-quoted state (verified `showParts:true`, `dc.html:9826` — a
       * LITERAL true, not gated on quote status). `QuotedLinesPanel` below
       * mounts the SAME `RfqPartsCard` for the quoted path
       * (`isReturned={true}`) — see that component's doc comment. This
       * replaces the old `.rfq-card-thumb--placeholder` box and the
       * `<ul className="rfq-card-lines">` text list, which ALSO used to
       * render (ungated) for a quoted RFQ, duplicating its parts under a
       * second "Parts in this request" heading above `QuotedLinesPanel`'s
       * own table — that double-listing is deleted, not just hidden. */}
      {!showsQuote && (
        <>
          <RfqPartsCard
            rows={nonQuotedPartsRows}
            isReturned={false}
            partCount={rfq.lines.length}
            onAddParts={() => setAddPartsDialog(true)}
            tableAriaLabel="Parts in this request"
            banner={submissionBanner}
            addPartsButtonRef={addPartsBtnRef}
          />
          {/* UID-611 review round (WCAG 4.1.3) — announces a line
           * quantity/removal outcome for assistive tech, the non-quoted
           * table's twin of `QuotedLinesPanel`'s pre-existing panel-level
           * `aria-live` region below. */}
          <p aria-live="polite" className="sr-only rfq-line-live-region">
            {lineLiveMessage}
          </p>
        </>
      )}
      {/* ADR-0115 D1 — `RfqCardQuoteSummary` (the Ship-by + best-option
       * block) is DELETED, not rescoped. ADR-0112 D1 already deleted the
       * dead `<ul>`/placeholder thumb from this body; this was the one
       * residue it didn't name. Ship-by and best-option have two other,
       * correct homes — the LIST row (`RfqListRow.tsx`, `dc.html:3485`/
       * `:3488`) and the detail RAIL (Lead time/Total, `dc.html:3703`/
       * `:3710`) — so rendering them a THIRD time here duplicated
       * information the design never duplicates. Gated on
       * `showsQuote && quote && quoteLoadState === 'loaded'` (same gate as
       * the deleted block), so it was unreachable after ADR-0112 landed and
       * would otherwise be dead code kept alive behind a false gate (the
       * ADR-0112 D7 discipline, applied to a component instead of a
       * stylesheet rule). */}

      {/* UID-294 (ADR-0104 D2) — the "Add parts" affordance opens the design's
       * "Add parts to this RFQ" picker (`AddPartsToRfqModal`,
       * `Fictiv Platform.dc.html:3630`): subtitle + search + catalog/library
       * rows (thumbnail · name · tag · MPN · price) + per-row Add + an Upload
       * / Done footer. UID-265b — `onAdd` now performs the REAL append
       * (`POST /rfq/{rfqId}/lines`, `appendPart`/`handleAddPart` above),
       * ported verbatim from `RfqDetailSummaryRail.tsx`'s `ActiveSummaryRail`
       * (same conflict classification, toast copy, and double-submit guard);
       * this supersedes the earlier demo-honest stub toast. "Upload new
       * files" hands off to the intake screen when the parent wires
       * `onUploadNewFiles` (RfqDetailPage → `/uploads`), else a demo toast —
       * unchanged by this fix. */}
      <AddPartsToRfqModal
        open={addPartsOpen}
        search={searchAddPartsCandidates}
        alreadyOnRfq={alreadyAddedSourceableIds}
        adding={addingPart}
        onClose={() => setAddPartsDialog(false)}
        onAdd={(sourceable) => void handleAddPart(sourceable)}
        onUploadNewFiles={
          onUploadNewFiles ??
          (() => showToast('Upload new files is a demo — not yet available.', 'info'))
        }
      />

      {/* Follow-up to UID-265b — the per-line configure modal for the
       * non-quoted parts table's Configure button above. CONDITIONALLY
       * mounted (only while `configureReviewLine` resolves, i.e. while
       * `configureLineId` names a line still present in `rfq.lines`) so a
       * reopen always starts from this line's latest server state, the
       * same reset-between-openings posture `RfqReviewPage`'s own mount
       * site for this modal documents. */}
      {configureReviewLine && (
        <RfqLineConfiguratorModal
          open
          line={configureReviewLine}
          onClose={() => setConfigureLineId(null)}
          onSave={handleSaveLineConfigure}
        />
      )}

      {/* `accepted` (not just `quoted`) also mounts the panel: accepting and
       * adding to cart are two separate, non-atomic calls (see the panel's
       * doc comment), and a page reload after a cart-add failure would
       * otherwise strand the customer with no way to retry — the panel's
       * per-line add is the retry affordance, and it survives a reload
       * because it's driven by the SERVER's `rfq.status`, not fragile local
       * state (defect found by QA's full-story walkthrough of UID-56). */}
      {showsQuote && (
        <QuotedLinesPanel
          rfqId={rfq.id}
          onChanged={onChanged}
          {...(onRfqDeleted ? { onRfqDeleted } : {})}
          quote={quote}
          loadState={quoteLoadState}
          specSelectionBySourceableId={specSelectionBySourceableId}
          partBySourceableId={partBySourceableId}
          quantityBySourceableId={quantityBySourceableId}
          filesBySourceableId={filesBySourceableId}
          partCount={rfq.lines.length}
          onAddParts={() => setAddPartsDialog(true)}
          selectedVariantId={effectiveSelectedVariantId}
          onVariantSelect={handleVariantSelect}
          banner={submissionBanner}
        />
      )}

      {(rfq.status === 'accepted' || rfq.status === 'declined') && (
        <p className="rfq-card-decision-note">
          {/* Deliberately NEUTRAL copy for `accepted`: this component has no
           * reliable signal for whether the cart-add that follows acceptance
           * actually landed (it's a separate call, and nothing persists that
           * outcome server-side) — asserting "the lines were added to your
           * cart" here would be a claim this card cannot back up. The real,
           * honest state (still pending / already there / needs retry) is
           * whatever the panel above renders. */}
          {rfq.status === 'accepted' ? 'You accepted this quote.' : 'You declined this quote.'}
        </p>
      )}

      {/* UID-294 §4 (ADR-0104 D3) — the standalone "Mark ready & share"
       * button (formerly `<ShareHandoffAction>`) is REMOVED from here: it is
       * not in the design, and the design carries exactly ONE share
       * affordance, "Share quote" on the sticky rail (named
       * "Generate & share quote" until UID-618)
       * (`RfqDetailSummaryRail.tsx`'s `ReturnedSummaryRail`), which now
       * reuses this SAME `useShareHandoff` flow (exported below) as its
       * entry point. This is a REWIRE, not a retirement — the "Ready to
       * share"/"Share link expired" header chips above still reflect
       * `handoff.subState`, unchanged. */}

      {/* UID-238 item 3 (Fictiv Platform.dc.html:3524-3529): the design shows
       * exactly ONE resubmit CTA for an expired RFQ, in the sticky rail
       * ("Resubmit RFQ", primary). `RfqCard`'s own `ReRequestAction`
       * ("Re-request quote", outline) used to ALSO render here, and because
       * `RfqCard` has exactly one mount site — `RfqDetailPage`, which never
       * wires the dedup props (`pendingReRequestDraftId`/
       * `onReRequestDraftCreated`/`onReRequestDraftCleared`) this action
       * needs — clicking both independently-tracked CTAs on the same page
       * could create two separate draft RFQs from one expired RFQ. Removing
       * this body action (rather than wiring the dedup props across the
       * detail page) both restores design fidelity and closes that
       * functional risk in one move — see ADR-0056's amendment for the
       * recorded choice. `ReturnedSummaryRail`'s sibling variant,
       * `ExpiredSummaryRail` ("Resubmit RFQ"), is the sole surviving CTA. */}
    </div>
  )
})

/**
 * UID-134 — the "Bundled" badge. A deliberate WCAG deviation from the design's
 * title-only tooltip (1.4.13 / 2.1.1 / 4.1.2): a real `<button>` (keyboard-
 * reachable, never hover-only) with a lock icon (decorative, aria-hidden) and
 * visible "Bundled" text, whose click/Enter/Space opens a dismissible
 * disclosure carrying the full bundle-note. Escape and outside-click dismiss;
 * Escape returns focus to the badge. The `title=` is a supplementary
 * mouse-only affordance, never the sole path. The note text is the shared
 * client-side constant (`RFQ_BUNDLED_NOTE`, UID-198/ADR-0074 — one canonical
 * string across the header badge, the list-row badge, and the detail-page
 * `RfqBundledCallout`), not a wire field.
 */
/**
 * UID-325 (AC6) — the quote-option selector, lifted out of
 * `QuotedLinesPanel` so `RfqCard` can mount it ABOVE its own body and match
 * the design's section order (quote options -> "Parts in this request" +
 * "Add parts" -> parts table, `dc.html:3520` then `:3559`).
 *
 * Hoisting the MOUNT POINT rather than deleting the body's parts list was the
 * deliberate choice: the literal reading of AC6 ("a parts-summary card ->
 * quote options -> parts table" becomes "quote options -> heading -> table")
 * would mean dropping `.rfq-card-lines`, and with it UID-267's per-line
 * supporting-file chips — which have NO equivalent in the table, because the
 * table's own drawing chip is an explicit ADR-0064 D2 deferral. Reordering
 * loses nothing; deleting would have lost a shipped feature to satisfy a
 * section order.
 *
 * The per-variant preview totals come from `quotePreview` below.
 *
 * Gate 5 correction: an earlier version of this comment (and ADR-0109 D6)
 * claimed `quotePreview` was "shared with the panel's row pricing, so the two
 * can never diverge". That was false — the panel's per-row prices go through
 * `effectiveLine`/`applyVariantRatio`, not through this helper, and did so
 * before this extraction too. What `quotePreview` actually guarantees is that
 * the SELECTOR's totals have one implementation instead of being inlined at
 * whatever surface renders the selector. Agreement with the cart POST still
 * rests on both paths using the same PER-LINE rounding — the UID-116 defect —
 * which is enforced by `quotePreview`'s own doc comment below and by the
 * UID-116/UID-230 tests, not by a shared call site. Do not restate the
 * stronger claim.
 *
 * ADR-0115 F1 — the design's returned detail body has TWO separate
 * `fictiv-card`s (`dc.html:3521` quote options, `:3560` parts), not one
 * merged card. `RfqPartsCard` (ADR-0112 D1) already owns the parts half;
 * this wraps the selector in its OWN `.quote-options-card`, carrying the
 * design's literal `border:1px solid #BDE9D8` — the options half of the
 * split. A bare `<div>`, not the shared `Card` component: `RfqPartsCard`
 * sets the same precedent (a plain scoped-class div, not a nested `Card`,
 * inside `RfqCard`'s own outer `Card`), and reusing `Card` here would stack
 * its own inset-shadow border underneath this literal one.
 */
function QuoteOptionsCard({
  quote,
  selectedVariantId,
  onSelect,
}: {
  readonly quote: Quote
  readonly selectedVariantId: QuoteVariant['id'] | undefined
  readonly onSelect: (id: QuoteVariant['id']) => void
}) {
  // Gate 5: was a third inline copy of `selectedVariantId ?? variants[0]?.id`
  // held together by a comment; now the shared `domain.ts` resolver, so this
  // surface and the panel/rail cannot resolve to different variants.
  const resolvedVariantId = resolveQuoteVariantId(quote, selectedVariantId)
  // Perf (no behavior change) — memoized on `quote` (the only input to
  // `quotePreview`, which re-derives from `quote.lines`/`quote.variants`).
  // Called unconditionally, ABOVE the early return below, so this hook call
  // is never skipped on some renders but not others.
  const { previewAmounts, baseCurrency } = useMemo(() => quotePreview(quote), [quote])
  if (!quote.variants || quote.variants.length === 0 || !resolvedVariantId) return null
  return (
    <div className="quote-options-card">
      <QuoteVariantSelector
        variants={quote.variants}
        selectedVariantId={resolvedVariantId}
        onSelect={onSelect}
        previewAmounts={previewAmounts}
        currency={baseCurrency}
      />
    </div>
  )
}

/**
 * The per-variant preview totals and the quote's base currency.
 *
 * UID-325 extracted this from `QuotedLinesPanel` when the selector was
 * hoisted, so ONE implementation feeds both surfaces. That single-source
 * property is load-bearing, not tidiness — see the rounding note below.
 *
 * UID-230 Item 1: reuse the SINGLE shared `isRowAddable` predicate (same as
 * `readyLines`) so the preview total sums exactly the lines that can actually
 * land in the cart. Filtering on `status === 'ready'` + `price` alone
 * (dropping the `deliveryPromise` requirement `isRowAddable`/the real
 * `/cart/lines` POST enforce) let a ready+priced line with NO
 * `deliveryPromise` — un-addable — inflate every region's preview total,
 * overstating the checkout price. The `.flatMap` still narrows `line.price`.
 *
 * QA (UID-116 defect) fix: the preview total for each variant MUST be the sum
 * of each line's INDIVIDUALLY-rounded scaled amount (the same per-line
 * rounding `effectiveLine` uses for the real `/cart/lines` POSTs) — never the
 * variant ratio applied once to the aggregate base total. Rounding half-up
 * per line then summing is not the same number as summing first and rounding
 * once (a $0.65 x2 / ratio 0.78 line pair rounds to $0.51 x2 = $1.02 per-line
 * vs. $1.01 if rounded once on the $1.30 aggregate) — so "what the selector
 * shows" must be built the exact same way as "what lands in the cart," or the
 * two silently diverge.
 */
function quotePreview(quote: Quote): {
  readonly previewAmounts: Record<QuoteVariant['id'], string>
  readonly baseCurrency: string
} {
  const readyPricedLines = quote.lines
    .filter(isRowAddable)
    .flatMap((line) => (line.price ? [{ price: line.price, quantity: line.quantity }] : []))
  const previewAmounts = Object.fromEntries(
    (quote.variants ?? []).map((variant) => [
      variant.id,
      readyPricedLines.length > 0
        ? addAmounts(
            readyPricedLines.map((l) =>
              multiplyAmount(applyVariantRatio(l.price.amount, variant.ratio), l.quantity.value),
            ),
          )
        : '0.00',
    ]),
  ) as Record<QuoteVariant['id'], string>
  return { previewAmounts, baseCurrency: readyPricedLines[0]?.price.currency ?? 'USD' }
}

/** Follow-up to UID-265b — the non-quoted parts table's per-row Configure
 * button, rendered into `RfqPartsCard`'s existing trailing actions cell.
 * Reuses `PartsReviewTable.tsx`'s own `.rfq-review-configure-btn` class
 * (the design-literal orange "Configure" button, `dc.html:4226`) rather
 * than a second copy of the same rule — that class is already scoped
 * generically (styling alone, no selector coupling to that file's own DOM
 * structure), so it applies here unchanged. */
function ConfigureLineButton({
  lineId,
  onConfigureLine,
}: {
  readonly lineId: string
  readonly onConfigureLine: (lineId: string) => void
}) {
  return (
    <button
      type="button"
      className="rfq-review-configure-btn"
      onClick={() => onConfigureLine(lineId)}
    >
      Configure
    </button>
  )
}

function BundledBadge() {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const noteId = useId()

  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node
      if (!popoverRef.current?.contains(target) && !buttonRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [open])

  return (
    <span className="rfq-card-bundled-wrap">
      <button
        type="button"
        ref={buttonRef}
        className="rfq-card-bundled-badge"
        aria-label="Bundled — why this quote is combined"
        aria-expanded={open}
        aria-controls={open ? noteId : undefined}
        title="Bundled — why this quote is combined"
        onClick={() => setOpen((current) => !current)}
      >
        <Icon name="lock" />
        <span>Bundled</span>
      </button>
      {open && (
        <div ref={popoverRef} id={noteId} className="rfq-card-bundled-popover">
          {RFQ_BUNDLED_NOTE}
        </div>
      )}
    </span>
  )
}

// ADR-0115 D1 — `RfqCardQuoteSummary` (the "Ship by {date}" + best-option
// unit-price block, formerly mounted in `.rfq-card-body` above) is DELETED,
// not rescoped: it was the one residue ADR-0112 D1 didn't name when it
// deleted the rest of `.rfq-card-body`'s dead content. See the removal note
// at this component's former mount site for the full rationale (two other,
// correct homes already exist — `RfqListRow`/the detail rail — so a third
// rendering here was pure duplication, unreachable behind a false gate).

/** Sub-states for the Act-3 handoff CTA on an `accepted` `RfqCard`
 * (UX spec §1.2): `not-ready` (no session minted yet — the default,
 * including while the background existence check is in flight or if it
 * fails, per §1.3's "fetch-existing error" row: fail open to `not-ready`
 * rather than blocking the card), `ready`, or `expired`. */
export type HandoffSubState = 'not-ready' | 'ready' | 'expired'

export interface ShareHandoff {
  readonly subState: HandoffSubState
  readonly session: SharedSession | undefined
  readonly modalOpen: boolean
  /** Set only on a USER-triggered mark-ready failure (never on the silent
   * background existence check) — rendered as an inline `role="alert"` next
   * to the CTA in addition to the toast, so the failure is visible even if
   * the toast has already auto-dismissed. */
  readonly markError: string | undefined
  /** UID-438 (ADR-0143 D6) — resolves TRUE on success. Widened from
   * `Promise<void>` so `RfqShareQuoteModal` can decide between its ready and
   * error states without reading a `markError` prop that has not re-rendered
   * yet. Additive for any caller that ignores the value; the only callers are
   * `RfqDetailSummaryRail`'s (this file exports the hook for the header chips
   * but never calls `markReady` itself). */
  readonly markReady: () => Promise<boolean>
  readonly openModal: () => void
  readonly closeModal: () => void
}

/* UID-438 (ADR-0143 D1) — `marking`, `modalFetchState` and `retryModalFetch`
 * were REMOVED from this interface, and `fetchSession` with them.
 *
 * Stated enumeration, verified by `grep -rn "handoff\.<member>"` over
 * `apps/web/src`: the surviving members have exactly these consumers —
 * `subState` → `RfqCard` (the header "Ready to share"/"expired" chips);
 * `session`, `modalOpen`, `openModal`, `closeModal`, `markReady`, `markError`
 * → `RfqDetailSummaryRail`. The three removed members had NONE once the modal
 * took over the mark-ready call:
 *   - `marking` drove the rail button's spinner, which now lives in the
 *     modal's generating state;
 *   - `modalFetchState`/`retryModalFetch` drove the retired modal's
 *     loading/error sub-states (UX spec §2.3), which ADR-0143 D6 collapsed.
 *
 * `openModal` no longer pre-fetches the session either. It used to fire
 * `GET /rfq/{rfqId}/session` and set `modalFetchState('error')` on failure —
 * which, after the rewire, NOTHING rendered, so a session-fetch failure was
 * silently swallowed. The modal does not need a pre-fetched session: it reads
 * one only in its ready state, which is reachable only after `markReady`
 * returns a fresh one. The background existence check in the effect above is
 * untouched — it is what feeds `subState` to `RfqCard`'s chips. */

/** Owns the Act-3 handoff session lifecycle for one RFQ card/rail
 * (UX spec §0/§1/§2): a background existence check on mount (silent per
 * §1.3), the mark-ready POST (idempotent — §1.3's "ready" row, re-marking an
 * already-ready RFQ just returns the same session, never a duplicate — see
 * `apps/api/openapi/paths/rfq.yaml`'s `markRfqReady` doc), and the modal's
 * own re-fetch path (§2.3 "Error"/"Loading", used by "View share link").
 *
 * UID-294 (ADR-0104 D3) — exported so `RfqDetailSummaryRail.tsx`'s
 * `ReturnedSummaryRail` can reuse this SAME flow as the "Generate & share
 * quote" button's entry point (the design's one share affordance), instead
 * of a second, rebuilt implementation. `RfqCard` keeps its own call (below)
 * to drive the header's "Ready to share"/"Share link expired" chips — the
 * two instances independently re-derive the same server state, the same
 * pattern this file's `useRfqQuote` already uses across sibling components
 * on this page. */
export function useShareHandoff(rfqId: string, enabled: boolean): ShareHandoff {
  const { showToast } = useToast()
  const [session, setSession] = useState<SharedSession | undefined>(undefined)
  const [modalOpen, setModalOpen] = useState(false)
  const [markError, setMarkError] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    async function checkExisting() {
      const { data } = await apiClient.GET('/rfq/{rfqId}/session', {
        params: { path: { rfqId } },
      })
      if (!cancelled && data) setSession(data)
      // 404/other error: fail open to `not-ready` (§1.3) — no state change.
    }
    void checkExisting()
    return () => {
      cancelled = true
    }
  }, [rfqId, enabled])

  async function markReady(): Promise<boolean> {
    setMarkError(undefined)
    try {
      const { data, error } = await apiClient.POST('/rfq/{rfqId}/mark-ready', {
        params: { path: { rfqId } },
      })
      if (!data) {
        const message =
          (error as { detail?: string } | undefined)?.detail ??
          "Couldn't mark this quote ready. Try again."
        setMarkError(message)
        showToast(message, 'error')
        return false
      }
      setSession(data)
      // UID-438 (ADR-0143 D1) — kept, not removed: `RfqShareQuoteModal` is
      // already open when it invokes this (the POST is now its "Generate
      // quote" action, not the rail button's `onClick`), so this is a no-op
      // there rather than a second opener.
      setModalOpen(true)
      return true
    } catch {
      // UID-438 (Gate-5 CRITICAL 1) — this `catch` did not exist: the function
      // was `try/finally`, so a THROWN fetch error escaped entirely. It really
      // can throw — `openapi-fetch` rethrows when `fetch` rejects and no
      // middleware handles it (`openapi-fetch/dist/index.js:198-200`), and
      // `lib/api/client.ts` registers no `onError`. The escape stranded
      // `RfqShareQuoteModal` on its generating state forever (no header, no
      // footer, zero focusable elements) AND left an unhandled rejection.
      //
      // Fixed HERE rather than only in the modal so every current and future
      // caller gets the same contract: `markReady` resolves false on failure,
      // it does not reject. The modal keeps its own `catch` as belt-and-braces.
      const message = "Couldn't mark this quote ready. Check your connection and try again."
      setMarkError(message)
      showToast(message, 'error')
      return false
    }
  }

  function openModal() {
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
  }

  const subState: HandoffSubState = !session
    ? 'not-ready'
    : isSharedSessionExpired(session)
      ? 'expired'
      : 'ready'

  return {
    subState,
    session,
    modalOpen,

    markError,
    markReady,
    openModal,
    closeModal,
  }
}

/** UID-108: the set of `sourceable.id`s already sitting in the current cart
 * that came FROM this specific Quote (`LineItem.sourceQuoteId`) — the real
 * "already added" signal, as opposed to inferring it from one POST call's
 * in-memory result. Fails open to an empty set (a cart-fetch failure means
 * "no confirmed adds," i.e. the pre-fix behavior of re-offering every ready
 * line) rather than blocking the card on a second, non-essential fetch. */
async function cartSourceableIdsForQuote(quoteId: string): Promise<ReadonlySet<string>> {
  const { data } = await apiClient.GET('/cart')
  if (!data) return new Set()
  return new Set(
    data.lines.filter((line) => line.sourceQuoteId === quoteId).map((line) => line.sourceable.id),
  )
}

/** Per-row add-to-cart status, keyed on `line.sourceable.id` — NEVER row
 * index (unstable across re-sort/re-filter/re-render, ADR-0064 D1). */
type RowAddStatus = 'idle' | 'in_flight' | 'added' | 'failed'

/** The result of the single-flight quote-accept sequencing (ADR-0064 D1a). */
type AcceptOutcome = { readonly ok: true } | { readonly ok: false; readonly message: string }

/** The SINGLE shared "can this line actually be added?" predicate (Gate 5
 * review, W2 fix) — `price` AND `deliveryPromise` are BOTH optional on
 * `LineItem`, and `handleRowAdd`'s actual `/cart/lines` POST (via
 * `effectiveLine`) requires both. Every other site that decides whether a
 * row counts as "addable" (the row's enabled/disabled Add button, the
 * mount-time status seeding, and the `readyLines`/`onChanged` gate) MUST use
 * this exact predicate — otherwise a `'ready'` line with a `price` but no
 * `deliveryPromise` renders an enabled button that can never succeed AND
 * permanently blocks `allInCart`/`onChanged` for every OTHER line on the
 * quote (the bug this fixes: three call sites previously disagreed).
 *
 * UID-302 fix: this is now a thin alias for the shared, exported
 * `isQuoteLineAddable` (`domain.ts`) — the exact same logic, consolidated
 * there so `subtotalForQuote` and `RfqDetailSummaryRail.tsx` can reuse it
 * instead of re-deriving it. Kept as a local name (rather than repointing
 * this file's five call sites at the import directly) to minimize churn. */
function isRowAddable(line: RfqLineItem): boolean {
  return isQuoteLineAddable(line)
}

/** Gate 5 (PR #164) finding #4: the one literal error string every `failed`
 * transition shows, in both the row's inline message and (via `handleRowAdd`
 * below) the customer-facing outcome — hoisted so the component and its
 * tests share the exact same string instead of three inline duplicates. */
export const ROW_ADD_ERROR_TEXT = "Couldn't add — try again."

/** Fetches the Quote for a `quoted` RFQ and renders its priced lines with a
 * PER-ROW instant add-to-cart button (UID-196; ADR-0064 supersedes the prior
 * bulk-select-checkbox + single bottom "Add N selected to cart" model — see
 * git history for that version). Accepting the quote and adding a line to
 * cart are two separate calls (UID-51 accept + UID-58 cart/lines, no combined
 * endpoint), so this component still owns making that non-atomic sequence
 * safe to retry — every property the old bulk flow had is preserved, just
 * re-derived PER ROW instead of panel-wide (ADR-0064 D1):
 *
 *  - **Single-flight accept.** The first row-add of the session triggers
 *    `POST /rfq/{rfqId}/quote/accept`; concurrent row-adds (the user can fire
 *    several before the first resolves, unlike the old sequential bulk loop)
 *    all `await` the SAME in-flight promise (`acceptPromiseRef`), so accept
 *    is never double-POSTed. A second accept would just 409 anyway, but the
 *    single-flight guard means that never happens. `accepted` (state) is the
 *    durable "already accepted" memory for later row-adds and for reload.
 *  - **Per-row 409-as-success.** Each row's own `POST /cart/lines` treats a
 *    409 ("already in cart," the contract's one-line-per-Sourceable rule) as
 *    `'added'`, never a failure to report — identical to the old panel-wide
 *    rule, just evaluated per row.
 *  - **`onChanged` fires once, only when every ready line is `'added'`.**
 *    The per-row analog of the old "`stillFailed.size === 0`" gate — a
 *    partial or total cart-add failure never advances the card, and a row
 *    that failed shows its own retry affordance, never a silent "added."
 *  - **UID-108 reload survival, per row.** On mount for an already-`accepted`
 *    quote, each ready line's status is seeded from the CART's own record of
 *    what it holds (`cartSourceableIdsForQuote`) — `'added'` if the cart
 *    already has it, `'idle'` otherwise — so a reload (or the same session
 *    right after a verified-successful add) renders true per-row state
 *    instead of re-offering a line that's already there.
 *  - **Same-row double-submit guarded in code**, not just via the button's
 *    `disabled` attribute — the click handler early-returns when that row's
 *    status is already `'in_flight'` or `'added'` (mirrors
 *    `VolumePricingPopover`'s `if (adding) return`). */
function QuotedLinesPanel({
  rfqId,
  onChanged,
  onRfqDeleted,
  quote,
  loadState,
  specSelectionBySourceableId,
  partBySourceableId,
  quantityBySourceableId,
  filesBySourceableId,
  partCount,
  onAddParts,
  selectedVariantId,
  onVariantSelect,
  banner,
}: {
  readonly rfqId: string
  // UID-611 (ADR-0178 D2/ADR-0029) — see `RfqCardProps.onChanged`'s doc
  // comment; this panel's quantity-PATCH/single-remove handlers call this
  // with the fresh `RfqView` to replace state wholesale, never recomputing
  // a total locally.
  readonly onChanged: (fresh?: Rfq) => void
  /** UID-611 (ADR-0178 D3) — see `RfqCardProps.onRfqDeleted`'s doc comment;
   * fired by this panel's single-remove and batch-remove handlers when a
   * removal cascade-deleted the RFQ. */
  readonly onRfqDeleted?: () => void
  // UID-134: the Quote and its load state are now fetched ONCE at the card
  // level (`useCardQuote`) and injected, so the row summary and this panel
  // share a single fetch. All the accept/add-to-cart/retry behavior below is
  // unchanged — only the source of `quote`/`loadState` moved.
  readonly quote: Quote | undefined
  readonly loadState: QuoteLoadState
  /** ADR-0115 D3 — `sourceable.id -> Configure-step material selection` for
   * the OWNING RFQ's lines (§ RfqCard doc comment above). Feeds the
   * Requirements column's `· material` suffix via `materialLabelForLine`. */
  readonly specSelectionBySourceableId: ReadonlyMap<string, RfqLineView['specSelection']>
  /** UID-289 (ADR-0093 §C2/§C5) — `sourceable.id -> part display identity`
   * for the OWNING RFQ's lines, the `specSelection` sibling of
   * `specSelectionBySourceableId` above (same reason: the Quote's own
   * `LineItem` carries neither field). */
  readonly partBySourceableId: ReadonlyMap<string, RfqLinePartView | undefined>
  /** ADR-0131 — `sourceable.id -> RFQ REQUEST line quantity` for the owning RFQ's lines. The
   *  effective quantity the row add posts to the cart, so an edited request quantity reaches
   *  the cart line rather than the Quote `LineItem`'s possibly-stale copy. */
  readonly quantityBySourceableId: ReadonlyMap<string, RfqLineView['quantity']>
  /** UID-327/328 (ADR-0112 D2) — `sourceable.id -> supporting-file chips`
   * for the OWNING RFQ's lines, the `files` sibling of the two maps above
   * (same reason). Rendered into `RfqPartsCard`'s "Name & drawing" cell —
   * the one disclosed visual addition the quoted path gains from this
   * extraction. */
  readonly filesBySourceableId: ReadonlyMap<string, readonly RfqLineFileView[] | undefined>
  /** UID-327/328 (ADR-0112 D1, code-review fix) — the RFQ's OWN line count
   * (`rfq.lines.length`), matching the design's `partCount:(r.parts?r.parts
   * .length:1)` (`dc.html:9826`, the RFQ's parts, not the Quote's). Normally
   * 1:1 with `quote.lines.length`, but `auto-quote-runner.ts`'s append path
   * can change the RFQ's line set without carrying the new lines into the
   * quote, so the RFQ's own count is the one that cannot under-count. */
  readonly partCount: number
  /** UID-327/328 (ADR-0112 D1) — opens `RfqCard`'s single, shared
   * `AddPartsToRfqModal` instance (owned there, not duplicated here) — the
   * quoted path's `RfqPartsCard` header needs the same "Add parts" button
   * the non-quoted path's does. */
  readonly onAddParts: () => void
  /** UID-238 item 2 — the pricing-region selection, now owned by `RfqCard`
   * (controlled/uncontrolled per its own doc comment) and injected here.
   * This panel keeps NO local selection state of its own any more —
   * `undefined` just means "nothing explicit yet," resolved to the first
   * variant at each point of use below, never re-seeded by an effect. */
  readonly selectedVariantId: QuoteVariant['id'] | undefined
  readonly onVariantSelect: (id: QuoteVariant['id']) => void
  /** UID-586 — threaded straight through from `RfqCard`'s own
   * `submissionBanner` prop; see `RfqCardProps`'s doc comment. */
  readonly banner?: ReactNode
}) {
  const { showToast } = useToast()
  const panelId = useId()
  // Per-row add status, keyed on `sourceable.id` (ADR-0064 D1). A ref mirror
  // is kept alongside the state so click handlers can read the CURRENT status
  // synchronously (no stale-closure risk across concurrent/rapid clicks) —
  // React state itself is only the render trigger.
  const rowStatusRef = useRef<ReadonlyMap<string, RowAddStatus>>(new Map())
  const [rowStatus, setRowStatus] = useState<ReadonlyMap<string, RowAddStatus>>(new Map())
  const [rowError, setRowError] = useState<ReadonlyMap<string, string>>(new Map())
  const [liveMessage, setLiveMessage] = useState('')
  const [accepted, setAccepted] = useState(false)
  // Single-flight guard for the quote-accept POST (ADR-0064 D1a): a row-add
  // that needs accept does `acceptPromiseRef.current ??= doAccept()` then
  // awaits it, so two concurrent first-clicks share the SAME promise and
  // exactly one accept POST fires. Cleared on failure so a later retry
  // re-attempts.
  const acceptPromiseRef = useRef<Promise<AcceptOutcome> | null>(null)
  // Guards `onChanged` (refetch, can unmount this panel) to fire at most once
  // per mount — the per-row analog of the old "stillFailed.size === 0" gate.
  const onChangedFiredRef = useRef(false)
  // UID-134: the panel's own "post-load processing done" flag. The quote fetch
  // now lives at the card level, but the accepted-state + cart cross-reference
  // that decide each row's initial status are still async and panel-local —
  // so the priced-lines table must not render until they resolve, or a row
  // would flash "Add" before flipping to "in cart" for an already-accepted
  // quote (the original self-fetching panel set `loaded` only AFTER this same
  // processing, so this preserves that ordering exactly).
  const [processed, setProcessed] = useState(false)
  // UID-294 §3 (ADR-0104 D2) — the reintroduced parts-table checkbox column,
  // keyed on `sourceable.id` (same key convention as `rowStatus` above).
  // Orthogonal to `rowStatus`/`accepted`/cart-add entirely: this selection
  // drives ONLY the demo-honest bulk "Move to new RFQ"/"Delete" bar below,
  // never the per-row instant-add flow (ADR-0064 D1, preserved unchanged).
  // UID-610 — `useRangeSelection` additionally carries the Gmail/Finder
  // shift-click anchor.
  const rangeSelection = useRangeSelection()
  const { selectedIds: selectedLineIds } = rangeSelection
  // Review-round fix (finding 8b, stale quote) — see `currentQuoteLines`'
  // doc comment below for why this is populated ONLY by this panel's own
  // successful removals, never derived from `rfq.lines` membership.
  const [locallyRemovedLineIds, setLocallyRemovedLineIds] = useState<ReadonlySet<string>>(new Set())
  const selectAllRef = useRef<HTMLInputElement | null>(null)
  // Reset selection whenever the underlying quote changes — the same reset
  // discipline every other per-quote-scoped piece of state in this file uses
  // (e.g. `RfqDetailSummaryRail.tsx`'s `editedQty`), so a selection made
  // against one quote can never bleed into another.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — reset WHEN quote.id changes; the body doesn't read it (stable clear()).
  useEffect(() => {
    rangeSelection.clear()
    // Review-round fix (finding 8b) — same reset discipline for
    // `locallyRemovedLineIds` (see `currentQuoteLines`' doc comment).
    setLocallyRemovedLineIds(new Set())
  }, [quote?.id])
  // The select-all checkbox's `:indeterminate` state is a DOM property, not
  // a React prop — set imperatively via the ref, mirroring the standard
  // React pattern for indeterminate checkboxes. Declared here (unconditional,
  // top-level) rather than after this component's early-return guards below,
  // so the Rules of Hooks are never violated by a loading/error render
  // skipping this effect on some renders but not others.
  useEffect(() => {
    if (!selectAllRef.current || !quote) return
    const ids = quote.lines.map((line) => line.sourceable.id)
    const allSelected = ids.length > 0 && ids.every((id) => selectedLineIds.has(id))
    selectAllRef.current.indeterminate = selectedLineIds.size > 0 && !allSelected
  }, [quote, selectedLineIds])

  // UID-611 (ADR-0178 D1/D5) — per-line busy trackers for THIS (quoted)
  // table's real quantity-PATCH and single-line DELETE, plus a bulk-delete
  // in-flight flag for the panel's own batch-remove button. Declared here,
  // above every early-return guard below, so the Rules of Hooks are never
  // violated (this file's own established discipline — see the surrounding
  // comments on why `quotedPartsRows` etc. sit AFTER the guards instead).
  const [qtyBusyIds, setQtyBusyIds] = useState<ReadonlySet<string>>(new Set())
  const [deleteBusyIds, setDeleteBusyIds] = useState<ReadonlySet<string>>(new Set())
  const [bulkDeleting, setBulkDeleting] = useState(false)
  // UID-611 review round (WCAG 2.4.3/4.1.3) — the quoted table's twin of
  // `RfqCard`'s own non-quoted focus-restore pair (see that component's
  // matching declaration for the full rationale): a removed row's kebab
  // trigger unmounts WITH the row, so focus must be moved deliberately
  // (here, to `RfqPartsCard`'s stable "Add parts" button) rather than
  // falling through to `<body>`. Reuses the panel's PRE-EXISTING
  // `liveMessage`/`setLiveMessage` (ADR-0064 D1's add-to-cart announcer) for
  // the announcement half — one live region, every outcome.
  const addPartsBtnRef = useRef<HTMLButtonElement | null>(null)
  const pendingFocusRestoreRef = useRef(false)
  // Keyed on `quantityBySourceableId` (not `quote`, which this panel never
  // refetches and so never changes reference after a removal) — that map is
  // rebuilt from the OWNING RFQ's `rfq.lines` at the parent on every
  // `onChanged`, so its identity changing is the actual signal that the
  // removed row is gone from the DOM and safe to focus past.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — re-run WHEN quantityBySourceableId changes; the body reads `pendingFocusRestoreRef`/`addPartsBtnRef` (refs, not reactive deps).
  useEffect(() => {
    if (!pendingFocusRestoreRef.current) return
    pendingFocusRestoreRef.current = false
    addPartsBtnRef.current?.focus()
  }, [quantityBySourceableId])

  /** Commits a per-row status transition to BOTH the ref (read synchronously
   * by click handlers) and React state (triggers the re-render). */
  function setRowStatusFor(id: string, status: RowAddStatus) {
    const next = new Map(rowStatusRef.current)
    next.set(id, status)
    rowStatusRef.current = next
    setRowStatus(next)
  }

  function clearRowError(id: string) {
    setRowError((current) => {
      if (!current.has(id)) return current
      const next = new Map(current)
      next.delete(id)
      return next
    })
  }

  function setRowErrorFor(id: string, message: string) {
    setRowError((current) => new Map(current).set(id, message))
  }

  /** Gate 5 (PR #164) finding #4: the one shared "this row failed" state
   * transition — status + inline error + live-region announcement —
   * `handleRowAdd` reaches this from three different failure points (accept
   * failure, a missing price/deliveryPromise, and a non-409 cart-POST
   * failure) and previously repeated all three calls inline at each site.
   * Deliberately does NOT fire the toast: that message differs per call site
   * (the accept error's own detail vs. the generic cart-add failure copy),
   * so callers still fire `showToast` themselves where one applies. */
  function failRow(id: string, partName: string) {
    setRowStatusFor(id, 'failed')
    setRowErrorFor(id, ROW_ADD_ERROR_TEXT)
    setLiveMessage(`Couldn't add ${partName} — retry available.`)
  }

  // UID-134: keyed on the injected `quote` rather than owning the fetch. Same
  // post-load processing the panel always did — reflect the SERVER's
  // accepted truth (never a redundant re-accept), and, for an
  // already-accepted quote, cross-reference the cart so each ready line's
  // initial status is `'added'` (already in the cart) or `'idle'` (the
  // UID-108 fix, now per row). UID-238 item 2: the variant default-seed that
  // used to live here is gone — `selectedVariantId` is now a prop (owned by
  // `RfqCard`), and every consumer below resolves `undefined` to the first
  // variant AT THE POINT OF USE (`resolvedVariantId`) instead of an async
  // effect mutating state after the fact. The `us` (identity ratio "1.00")
  // default is unchanged; only how it's arrived at is.
  useEffect(() => {
    if (!quote) return
    let cancelled = false
    async function process(data: Quote) {
      const readyLines = data.lines.filter(isRowAddable)
      const isAccepted = data.state === 'accepted'
      setAccepted(isAccepted)
      const seeded = new Map<string, RowAddStatus>()
      if (isAccepted) {
        // UID-108: an already-accepted quote's lines may already be sitting in
        // the cart from an earlier successful add (this mount could be a
        // reload, or a re-render after a partial retry) — cross-reference the
        // CART's own record of what it holds (`LineItem.sourceQuoteId`,
        // additive/frozen, ADR-0030) instead of assuming every `ready` line
        // still needs adding. This is the fix for the "Retry N to cart" that
        // never clears even after a verified-successful add.
        const alreadyInCart = await cartSourceableIdsForQuote(data.id)
        if (cancelled) return
        for (const line of readyLines) {
          seeded.set(line.sourceable.id, alreadyInCart.has(line.sourceable.id) ? 'added' : 'idle')
        }
      } else {
        for (const line of readyLines) {
          seeded.set(line.sourceable.id, 'idle')
        }
      }
      if (cancelled) return
      rowStatusRef.current = seeded
      setRowStatus(seeded)
      setProcessed(true)
    }
    void process(quote)
    return () => {
      cancelled = true
    }
  }, [quote])

  // UID-116 (ADR-0051) / UID-238 item 2: `resolvedVariantId` is the ONE
  // place this component falls back to the first variant — `selectedVariantId`
  // (the prop) stays `undefined` whenever nothing has been explicitly picked
  // yet, and this resolves that to `quote.variants?.[0]?.id` at the point of
  // use, never via a seeded/mutated piece of state. `selectedVariant` is
  // `undefined` whenever this quote carries no `variants` at all, which
  // keeps every price/deliveryPromise computation below an inert pass-
  // through (`effectiveLine`) for a pre-UID-116 quote.
  // UID-325 (Gate 5) — via the shared `domain.ts` resolver rather than an
  // inline copy; the point-of-use rule above is exactly what it encodes.
  const resolvedVariantId = resolveQuoteVariantId(quote, selectedVariantId)
  const selectedVariant = quote?.variants?.find((v) => v.id === resolvedVariantId)

  /** The price/deliveryPromise that will actually be POSTed to `/cart/lines`
   * for this line — price is scaled by the selected variant's ratio when one
   * is selected, otherwise the base priced line untouched. Used by BOTH the
   * visible table row and `handleAddToCart`, so what the customer sees
   * selected is guaranteed to equal what lands in the cart (never two
   * separate derivations that could drift apart).
   *
   * `deliveryPromise` is ALWAYS the line's own — untouched, unconditionally,
   * regardless of variant selection (ADR-0179 R1). A variant is a regional
   * pricing adjustment, not a replacement lead time; it must never rewrite a
   * line's own kind-based committed date. Collapsing every line onto
   * `committedDateForVariant(...)` destroyed the per-kind distinction the
   * backend now computes (auto-quote-policy.ts / cpq-writeback.ts) and made
   * UID-617's checkout delivery-plan step structurally unreachable. */
  function effectiveLine(line: RfqLineItem): {
    readonly price: RfqLineItem['price']
    readonly deliveryPromise: RfqLineItem['deliveryPromise']
  } {
    if (!selectedVariant || !line.price) {
      return { price: line.price, deliveryPromise: line.deliveryPromise }
    }
    return {
      price: { ...line.price, amount: applyVariantRatio(line.price.amount, selectedVariant.ratio) },
      deliveryPromise: line.deliveryPromise,
    }
  }

  /** Single-flight accept (ADR-0064 D1a): `accepted` short-circuits once the
   * server has confirmed it; otherwise the FIRST caller mints the in-flight
   * promise and every concurrent caller awaits that SAME promise, so accept
   * is POSTed at most once no matter how many rows are clicked before the
   * first resolves. */
  function ensureQuoteAccepted(): Promise<AcceptOutcome> {
    if (accepted) return Promise.resolve({ ok: true })
    if (!acceptPromiseRef.current) {
      acceptPromiseRef.current = (async (): Promise<AcceptOutcome> => {
        const accept = await apiClient.POST('/rfq/{rfqId}/quote/accept', {
          params: { path: { rfqId } },
        })
        if (!accept.data) {
          // Clear the guard so a later retry re-attempts the accept call.
          acceptPromiseRef.current = null
          return {
            ok: false,
            message: accept.error?.detail ?? 'Could not accept this quote. Please try again.',
          }
        }
        setAccepted(true)
        return { ok: true }
      })()
    }
    return acceptPromiseRef.current
  }

  /** Fires `onChanged` (refetch, can unmount this panel) exactly once, only
   * once every ready line has reached `'added'` — the per-row analog of the
   * old "stillFailed.size === 0" gate (ADR-0064 D1c). Reads `rowStatusRef`
   * (not React state) so it always sees the status just committed by the
   * caller, never a stale render. */
  function maybeFireOnChanged(currentQuote: Quote) {
    if (onChangedFiredRef.current) return
    // Review-round fix (finding 8b, stale quote) — `currentQuote.lines` is
    // this panel's OWN cached snapshot, which does not shrink when a line is
    // removed from the RFQ (the Quote itself is intentionally never
    // invalidated/unlinked by a removal, ADR-0178 D2/D3). Filtering out this
    // panel's OWN `locallyRemovedLineIds` (see `currentQuoteLines`' doc
    // comment above) is what keeps this gate from waiting forever on a line
    // that was just removed.
    const readyIds = currentQuote.lines
      .filter(isRowAddable)
      .filter((line) => !locallyRemovedLineIds.has(line.sourceable.id))
      .map((line) => line.sourceable.id)
    const allAdded =
      readyIds.length > 0 && readyIds.every((id) => rowStatusRef.current.get(id) === 'added')
    if (allAdded) {
      onChangedFiredRef.current = true
      onChanged()
    }
  }

  /** One row's instant add-to-cart (ADR-0064 D1b/D1d). Guards same-row
   * double-submit IN CODE (not only via the button's `disabled`): a click
   * while this row is already `'in_flight'` or `'added'` is a no-op. */
  async function handleRowAdd(line: RfqLineItem) {
    if (!quote) return
    const id = line.sourceable.id
    const currentStatus = rowStatusRef.current.get(id)
    if (currentStatus === 'in_flight' || currentStatus === 'added') return

    const partName = displayNameFor(line.sourceable)
    setRowStatusFor(id, 'in_flight')
    clearRowError(id)

    const acceptOutcome = await ensureQuoteAccepted()
    if (!acceptOutcome.ok) {
      failRow(id, partName)
      showToast(acceptOutcome.message, 'error')
      return
    }

    const { price, deliveryPromise } = effectiveLine(line)
    if (!price || !deliveryPromise) {
      failRow(id, partName)
      return
    }

    const { data, error } = await apiClient.POST('/cart/lines', {
      // Resolve the bare SourceableRef the quote line carries to its full
      // fixture Sourceable (name + catalogSku) so toAddCartLineRequest sends
      // the human part labels — otherwise the BFF persists no label and the
      // cart degrades to rendering the raw sourceable UUID (UID-212 bug).
      //
      // UID-424: `findSourceableById` misses for a real part the demo
      // fixture catalog doesn't know about, so pass the RFQ line's OWN
      // `part.name`/`part.partNumber` (`partBySourceableId`, sourced from
      // `rfq.lines`, the SAME label the RFQ screen itself renders — see
      // `line.part?.name ?? displayNameFor(line.sourceable)` above) as the
      // fallback label. It only wins when the resolved Sourceable carries no
      // name/sku of its own — a catalog part keeps sending its fixture
      // labels unchanged.
      body: toAddCartLineRequest(
        findSourceableById(line.sourceable.id) ?? line.sourceable,
        // ADR-0131: post the EFFECTIVE (RFQ request) quantity, not the Quote line's copy.
        quantityBySourceableId.get(line.sourceable.id) ?? line.quantity,
        {
          price,
          deliveryPromise,
          sourceQuoteId: quote.id,
          sourceRfqId: rfqId,
        },
        {
          name: partBySourceableId.get(id)?.name ?? undefined,
          partNumber: partBySourceableId.get(id)?.partNumber ?? undefined,
        },
      ),
    })
    // A 409 here means "one line per Sourceable, and this one's already in
    // the cart" (the contract's duplicate-line rule) — on a retry (e.g.
    // after a reload with no reliable signal of what already landed) that is
    // the SAME outcome as a fresh add from this customer's point of view,
    // not a failure to surface.
    const alreadyInCart = (error as { status?: number } | undefined)?.status === 409
    if (data || alreadyInCart) {
      // ADR-0131 A2 amendment — carry the accepted pricing variant into the cart's delivery
      // option so a per-row add is source-consistent with a full checkout (Cart page == Checkout
      // shipping). The variant is RFQ-level; best-effort, never fails the add.
      const deliveryOptionId = deliveryOptionForVariant(selectedVariant?.id)
      if (deliveryOptionId !== undefined) {
        // Truly best-effort: a failure here must neither abort the add (the
        // line is already in the cart) nor surface as an unhandled rejection.
        try {
          await apiClient.PUT('/cart/delivery-option', { body: { deliveryOptionId } })
        } catch {
          // Swallow — the variant delivery-option sync is a nicety, not a
          // precondition for the add having succeeded.
        }
      }
      setRowStatusFor(id, 'added')
      setLiveMessage(`Added ${partName} to cart.`)
      maybeFireOnChanged(quote)
    } else {
      failRow(id, partName)
      // Additive to (not a replacement of) the inline row error above — a
      // toast so an off-screen row's failure is never missed (UX spec §2.4).
      showToast(`Couldn't add ${partName} to your cart. Try again.`, 'error')
    }
  }

  if (loadState === 'error' || (loadState === 'loaded' && !quote)) {
    return <p role="alert">Couldn&rsquo;t load this quote. Try refreshing the page.</p>
  }
  // Hold the priced-lines table until the quote is fetched AND the panel's own
  // accepted/cart cross-reference processing has run (UID-134 ordering — see
  // `processed`), so the CTA is correct on first paint.
  if (loadState === 'loading' || !quote || !processed) {
    // UID-137 §2.5 (ADR-0060 §1/§2): `fxspin`, reusing the existing
    // `.spinner`/`Spinner` (byte-identical `spin` keyframe already wired to
    // `Button`'s own `loading` state) — no new keyframe, just filling a gap
    // this text-only loading line never had.
    return (
      <p className="rfq-card-loading">
        <Spinner size="sm" aria-hidden="true" /> Loading your quote…
      </p>
    )
  }

  // UID-294 §4 (ADR-0104 D3): `readyCount`/`addedCount`/`remainingCount`/
  // `allInCart` — computed here solely to feed the two visible "Quote
  // accepted…"/"All N in cart" status banners — are REMOVED along with
  // those banners (see the render below); nothing else in this component
  // read them.

  // UID-325 — `hasVariants` moved with the selector: the "only render when
  // the quote actually carries variants" guard (UID-116/ADR-0051, for a
  // pre-UID-116 quote with none) now lives in `QuoteOptionsCard`, which is
  // the only thing that ever consumed it. RfqCard.test.tsx's "no variants"
  // case still guards the behaviour.

  // UID-294 §3 (ADR-0104 D2) — checkbox-column selection derivations. Every
  // quoted line is selectable here (orthogonal to `isRowAddable`/cart
  // status — bulk move/delete is a DIFFERENT purpose than add-to-cart, per
  // the ADR).
  // Perf note: deliberately NOT wrapped in `useMemo` — this sits AFTER this
  // component's own early `return` guards above (loading/error), so adding
  // a new hook call here would make a hook conditional (Rules of Hooks),
  // which is a behavior/timing risk this ticket's scope explicitly excludes.
  // `quotedPartsRows` below (and the map it feeds) is skipped for the same
  // reason, plus its dependency list would include per-render-recreated
  // closures (`toggleLineSelected`/`handleRowAdd`), which would make the
  // memo re-run every render anyway.
  // Review-round fix (finding 8b, stale quote) — `quote.lines` is this
  // panel's own cached, never-refetched snapshot; a line the customer just
  // removed from the RFQ (`handleLineRemove`/`handleBulkDelete` below) stays
  // in it forever, because the Quote itself is deliberately never
  // invalidated/unlinked by a removal (ADR-0178 D2/D3's own contract note).
  // Filtered by `locallyRemovedLineIds` — populated ONLY by this panel's own
  // successful removes below, never by cross-referencing `rfq.lines` (a
  // quote's line SET does not always mirror the RFQ's 1:1 in every fixture/
  // edge case this file's OWN test suite already exercises — e.g. the Gate 5
  // W2 "incomplete line" case, a quote line with no matching `rfq.lines`
  // entry at all — so filtering on THAT membership would wrongly hide a row
  // that was never removed). This is what makes the rendered table (and its
  // checkbox/bulk-delete selection) follow the server's authoritative
  // "what's still on this request" answer instead of the frozen quote
  // snapshot, without any assumption about how closely `quote.lines` and
  // `rfq.lines` otherwise track each other.
  const currentQuoteLines = quote.lines.filter(
    (line) => !locallyRemovedLineIds.has(line.sourceable.id),
  )
  const selectableLineIds = currentQuoteLines.map((line) => line.sourceable.id)
  const allLinesSelected =
    selectableLineIds.length > 0 && selectableLineIds.every((id) => selectedLineIds.has(id))
  const someLinesSelected = selectedLineIds.size > 0 && !allLinesSelected

  /** UID-610 — shift-click range selection over this table's rendered row
   * order (Gmail/Finder semantics, same as UID-470's Parts Library).
   * `shiftKey` reaches here from the row checkbox's `onClick`, not
   * `onChange` (a `ChangeEvent` carries no modifier-key state). */
  function toggleLineSelected(id: string, shiftKey: boolean) {
    rangeSelection.toggle(id, selectableLineIds, shiftKey)
  }

  function toggleSelectAll() {
    rangeSelection.toggleAll(selectableLineIds, !allLinesSelected)
  }

  function clearSelection() {
    rangeSelection.clear()
  }

  /** DEMO-HONEST (ADR-0104 D2), UNCHANGED by ADR-0179 D1: "Move to a new
   * RFQ" stays exactly as shipped — real selection state + a real bar, but
   * the action itself fires an honest toast and performs NO mutation. No
   * bulk-move endpoint was authorized by ADR-0178 or ADR-0179; conflating
   * this with the real bulk DELETE below would be scope creep beyond what
   * either ADR decided. */
  function handleBulkMove() {
    const count = selectedLineIds.size
    showToast(
      `Moved ${count} part${count === 1 ? '' : 's'} to a new RFQ (demo) — no real RFQ was created.`,
      'info',
    )
    clearSelection()
  }

  /** UID-611 (ADR-0178 D1/D3, ADR-0179 D1) — retires the demo toast: fires
   * the real `POST /rfq/{rfqId}/lines/remove-batch` with every selected
   * line id, on every status (no status gating). Branches on the ADR-0178
   * D3 cascade the same way the single-line remove below does: if the
   * batch took the RFQ's last line, the RFQ itself was deleted and the
   * caller must navigate away (`onRfqDeleted`) instead of re-rendering a
   * now-stale view. */
  async function handleBulkDelete() {
    const ids = [...selectedLineIds]
    if (ids.length === 0) return
    setBulkDeleting(true)
    try {
      const { data, error } = await apiClient.POST('/rfq/{rfqId}/lines/remove-batch', {
        params: { path: { rfqId } },
        body: { lineIds: ids },
      })
      if (data) {
        if (data.rfqDeleted) {
          onRfqDeleted?.()
          return
        }
        const count = ids.length
        const message = `Removed ${count} part${count === 1 ? '' : 's'} from this request.`
        showToast(message, 'success')
        setLiveMessage(message)
        pendingFocusRestoreRef.current = true
        // Review-round fix (finding 8b, stale quote) — drop every batch id
        // from the RENDERED table immediately; see `currentQuoteLines`' doc
        // comment above.
        setLocallyRemovedLineIds((current) => new Set([...current, ...ids]))
        if (data.rfq) {
          onChanged(data.rfq)
        }
        clearSelection()
      } else {
        showToast(
          error?.detail ?? 'Could not remove the selected parts. Please try again.',
          'error',
        )
      }
    } catch {
      // Review-round fix (finding 7, swallowed network errors) — a thrown
      // fetch failure (offline, DNS) used to escape this `try`/`finally`
      // uncaught, with no toast at all.
      showToast(
        'Could not remove the selected parts. Check your connection and try again.',
        'error',
      )
    } finally {
      setBulkDeleting(false)
    }
  }

  /** UID-611 (ADR-0178 D1/D2) — the quoted table's twin of `RfqCard`'s own
   * `handleLineQuantityChange` (same endpoint, same "replace the whole view
   * from the server's response, never recompute locally" contract). Kept as
   * a separate copy in this separate component rather than a shared hook —
   * the two tables' busy-state maps are intentionally distinct (a line is
   * ever on ONE of the two tables at a time for a given RFQ status).
   *
   * Review-round fixes (UID-611) — resolves `boolean` (rollback signal for
   * `RfqLineQtyInput`) and is wrapped in a real `try`/`catch` (a thrown fetch
   * failure used to escape uncaught, with no toast) — same reasoning as
   * `RfqCard`'s own copy of this handler. */
  async function handleLineQuantityChange(
    lineId: string,
    uom: Quantity['uom'],
    nextValue: number,
  ): Promise<boolean> {
    setQtyBusyIds((current) => new Set([...current, lineId]))
    try {
      const { data, error } = await apiClient.PATCH('/rfq/{rfqId}/lines/{lineId}', {
        params: { path: { rfqId, lineId } },
        body: { quantity: { value: nextValue, uom } },
      })
      if (data) {
        onChanged(data)
        return true
      }
      showToast(error?.detail ?? 'Could not update quantity. Please try again.', 'error')
      return false
    } catch {
      showToast('Could not update quantity. Check your connection and try again.', 'error')
      return false
    } finally {
      setQtyBusyIds((current) => {
        const next = new Set(current)
        next.delete(lineId)
        return next
      })
    }
  }

  /** UID-611 (ADR-0178 D1/D3, ADR-0179 D1) — the quoted table's twin of
   * `RfqCard`'s own `handleLineRemove`; see that function's doc comment.
   * Review-round fix — real `try`/`catch`, same reasoning as above. */
  async function handleLineRemove(lineId: string, partName: string) {
    setDeleteBusyIds((current) => new Set([...current, lineId]))
    try {
      const { data, error } = await apiClient.DELETE('/rfq/{rfqId}/lines/{lineId}', {
        params: { path: { rfqId, lineId } },
      })
      if (data) {
        if (data.rfqDeleted) {
          onRfqDeleted?.()
          return
        }
        if (data.rfq) {
          onChanged(data.rfq)
        }
        showToast(`Removed “${partName}” from this request.`, 'success')
        setLiveMessage(`Removed ${partName} from this request.`)
        pendingFocusRestoreRef.current = true
        // Review-round fix (finding 8b, stale quote) — drop this line from
        // the RENDERED table immediately; see `currentQuoteLines`' doc
        // comment above.
        setLocallyRemovedLineIds((current) => new Set([...current, lineId]))
      } else {
        showToast(error?.detail ?? `Couldn't remove “${partName}”. Please try again.`, 'error')
      }
    } catch {
      showToast(`Couldn't remove “${partName}”. Check your connection and try again.`, 'error')
    } finally {
      setDeleteBusyIds((current) => {
        const next = new Set(current)
        next.delete(lineId)
        return next
      })
    }
  }

  // UID-327/328 (ADR-0112 D1) — the quoted path's `RfqPartsCard` rows. Every
  // per-row bit of INTERACTIVE state (checkbox, add-to-cart button, "More
  // actions" menu, error message) is still fully owned and rendered by THIS
  // panel — only the markup they render into moved into the shared
  // component. `RfqPartsCard` never sees `rowStatusRef`/`acceptPromiseRef`/
  // any of the other ADR-0064 D1 retry-safety state; it only receives
  // already-rendered nodes for the `actions`/`errorMessage` slots.
  const quotedPartsRows: readonly RfqPartsCardRow[] = currentQuoteLines.map((line, index) => {
    const partName = displayNameFor(line.sourceable)
    const part = partBySourceableId.get(line.sourceable.id)
    const resolvedPartName = part?.name ?? partName
    const displayPrice = effectiveLine(line).price
    // Review-round fix (finding 8a, stale quote) — same map the `quantity`
    // field below already reads; hoisted so the Unit price cell's line
    // subtotal (below) and the quantity input use the SAME live value
    // rather than two separate lookups that could drift.
    const liveQuantity = quantityBySourceableId.get(line.sourceable.id) ?? line.quantity
    const status = rowStatus.get(line.sourceable.id)
    const errorMessage = rowError.get(line.sourceable.id)
    const errorMessageId = `${panelId}-row-error-${line.sourceable.id}`
    const files = filesBySourceableId.get(line.sourceable.id)
    const notAddable = !isRowAddable(line)
    return {
      key: `${line.sourceable.id}-${index}`,
      thumbnailRef: part?.thumbnailRef ?? null,
      name: resolvedPartName,
      caption: partCaption(part),
      // UID-327/328 (ADR-0112 D2) — the one disclosed visual addition the
      // quoted path gains: file chips it never rendered before.
      fileChips: (() => {
        const drawingFiles = drawingFilesOnly(files)
        return drawingFiles && drawingFiles.length > 0 ? (
          <div className="rfq-card-line-files">
            {drawingFiles.map((file, fileIndex) => (
              <RfqLineFileChip
                key={file.id ?? `${line.sourceable.id}-file-${fileIndex}`}
                partId={line.sourceable.id}
                file={file}
                onDownloadError={(message) => showToast(message, 'error')}
              />
            ))}
          </div>
        ) : undefined
      })(),
      requirementsCell: (
        <RequirementsCell
          process={part?.process ?? null}
          material={materialLabelForLine(specSelectionBySourceableId.get(line.sourceable.id))}
        />
      ),
      // Review-round fix (finding 8a, stale quote) — `line.quantity` is this
      // panel's own cached, never-refetched Quote snapshot's copy, which does
      // NOT reflect a quantity edit made through THIS SAME input: after a
      // successful PATCH, `onChanged(data)` updates `rfq` (and therefore
      // `quantityBySourceableId`, sourced from it) but never re-fetches
      // `quote` — so the input used to visibly SNAP BACK to the pre-edit
      // value on the very next render. `quantityBySourceableId` (ADR-0131,
      // already the correct source for the cart-add POST body above) is the
      // fix: it always reflects the RFQ's current, just-edited quantity.
      quantity: liveQuantity,
      // UID-611 (ADR-0178 D1/D5) — the real, persisted quantity input, on
      // every quoted line, no status gating. Review-round fix — returns the
      // handler's own `Promise<boolean>` (not `void`-wrapped) so
      // `RfqLineQtyInput` can roll back its optimistic display on failure.
      onQuantityChange: (next: number) =>
        handleLineQuantityChange(line.sourceable.id, line.quantity.uom, next),
      quantityBusy: qtyBusyIds.has(line.sourceable.id),
      // Preserves the pre-extraction QuoteLineRow behavior exactly (an em
      // dash, not an empty cell, for a `ready` line with no price yet) — the
      // ADR-0112 D1 "empty cell" rule governs the NON-returned case, where
      // there is no price data at all; a quoted row that IS priced but
      // momentarily lacks `displayPrice` keeps the existing fallback so the
      // quoted path stays visually unchanged. UI fix (2026-09-11, human
      // ruling): plain per-unit price only — no "/EA" suffix, no subtotal.
      //
      // TEMPORARY (demo reversal, revertable) — `previewDiscountedUnitPrice`
      // applies the same volume-break curve the backend's `rfq-cost-
      // estimate.ts` now applies (see that file's doc comment), against the
      // LIVE quantity, so this cell visibly reprices on a quantity edit
      // instead of showing the frozen quote price forever. See `rfq-volume-
      // break-preview.ts`'s own doc comment for the one-line revert.
      unitPriceLabel: displayPrice
        ? formatMoney(
            previewDiscountedUnitPrice(displayPrice.amount, liveQuantity.value),
            displayPrice.currency,
          )
        : '—',
      checkbox: {
        checked: selectedLineIds.has(line.sourceable.id),
        onToggle: (shiftKey) => toggleLineSelected(line.sourceable.id, shiftKey),
        ariaLabel: `Select ${resolvedPartName}`,
      },
      actions: (
        <>
          {notAddable ? (
            <Button
              type="button"
              variant="purple"
              className="rfq-row-add-btn rfq-row-add-btn--unavailable"
              aria-label={`${resolvedPartName} isn't ready to add yet`}
              disabled
            >
              <Icon name="plus" />
            </Button>
          ) : (
            <RowAddToCartButton
              partName={resolvedPartName}
              status={status ?? 'idle'}
              errorMessageId={errorMessageId}
              onAdd={() => void handleRowAdd(line)}
            />
          )}
          <MoreActionsButton
            partName={resolvedPartName}
            onRemove={() => void handleLineRemove(line.sourceable.id, resolvedPartName)}
            busy={deleteBusyIds.has(line.sourceable.id)}
          />
        </>
      ),
      errorMessage: errorMessage ? (
        // Gate 5 (PR #164) finding #1 — see `RfqCard.test.tsx`'s
        // corresponding assertion: NO live-region role here on purpose, the
        // panel-level `aria-live="polite"` region below is the SOLE
        // announcement path.
        <p id={errorMessageId} className="caption-text rfq-row-add-error">
          {errorMessage}
        </p>
      ) : undefined,
    }
  })

  return (
    // UID-327/328 (ADR-0112 D1, code-review fix) — renamed from the retired
    // `.rfq-quote-lines`: that class carried this panel's table styling
    // before D1 moved the table into `RfqPartsCard` (whose own rules now
    // live under `.rfq-parts-card`, see `rfq.css`). This wrapper's only
    // remaining job is grouping the card + bulk bar + `aria-live` region
    // into one returned root; it carries no layout rules of its own, so the
    // rename is cosmetic, not a CSS dependency.
    <div className="rfq-quoted-lines-panel">
      {/* UID-238 item 4 (Fictiv Platform.dc.html:3510): design specifies ONE
       * Download-quote button, in the sticky rail
       * (`RfqDetailSummaryRail.tsx`'s `ReturnedSummaryRail`, wired to the
       * same honest demo toast). This panel's own copy — duplicated on the
       * detail page since ADR-0056 mounted both surfaces together — is
       * removed here rather than in the rail, per this ticket's fix
       * direction. */}
      {/* UID-325 (AC6) — the quote-option selector no longer renders here. The
       * design orders the detail body quote options -> "Parts in this
       * request" -> parts table (`dc.html:3520` then `:3559`), so `RfqCard`
       * mounts `<QuoteOptionsCard>` ABOVE its own body instead. Only the
       * mount point moved: the selection state is still owned by `RfqCard`
       * and the preview amounts still come from `quotePreview`, which this
       * panel's own row pricing shares. */}
      {/* UID-294 §4 (ADR-0104 D3) — the "Quote accepted — finish adding…"/
       * "All N lines are in your cart" visible status banners are REMOVED
       * (not in the design). The per-row add OUTCOME announcement they sat
       * next to is NOT removed — it lives in the panel-level `aria-live`
       * region below (ADR-0064 D1), unchanged. */}
      {/* UID-327/328 (ADR-0112 D1) — the panel's own inline `<table>` moved
       * into the shared `RfqPartsCard` (`isReturned={true}`); this panel
       * keeps every bit of the retry-safety state above and passes it
       * through as already-rendered `actions`/`errorMessage` nodes. */}
      <RfqPartsCard
        rows={quotedPartsRows}
        isReturned={true}
        partCount={partCount}
        onAddParts={onAddParts}
        tableAriaLabel="Quoted lines"
        addPartsButtonRef={addPartsBtnRef}
        bulkSelectAll={{
          checked: allLinesSelected,
          indeterminate: someLinesSelected,
          onToggle: toggleSelectAll,
          inputRef: selectAllRef,
        }}
        banner={banner}
        // UID-294 §3 (ADR-0104 D2, `Fictiv Platform.dc.html:3618-3626`) — the
        // demo-honest bulk action bar, shown once >=1 part is selected. Real
        // selection state + a real bar, but "Move"/"Delete" fire an honest
        // toast and perform NO mutation (see the handlers' doc comments).
        // Defect fix: rendered THROUGH `RfqPartsCard`'s `bulkBar` slot (not
        // as a sibling after it) so this strip shares the card's own
        // border/background instead of floating as a separate box below it
        // — confirmed against the design, which nests this bar in the SAME
        // card container as the rows (`:3617-3624`).
        bulkBar={
          selectedLineIds.size > 0 ? (
            <div
              className="rfq-parts-bulk-bar"
              // A generic ARIA "group" of bulk-action controls, not a form —
              // <fieldset> would bring unwanted default browser border/padding
              // (same reasoning/precedent as Avatar.tsx's `AvatarStack`).
              // biome-ignore lint/a11y/useSemanticElements: see comment above.
              role="group"
              aria-label="Bulk part actions"
            >
              <span className="rfq-parts-bulk-count">{selectedLineIds.size} selected</span>
              <span className="rfq-parts-bulk-spacer" />
              <Button variant="outline-purple" size="sm" onClick={handleBulkMove}>
                Move {selectedLineIds.size} to new RFQ
              </Button>
              <Button
                variant="danger-outline"
                size="sm"
                disabled={bulkDeleting}
                onClick={() => void handleBulkDelete()}
              >
                Delete
              </Button>
              <button type="button" className="rfq-parts-bulk-clear-btn" onClick={clearSelection}>
                Clear
              </button>
            </div>
          ) : undefined
        }
      />
      {/* Panel-level `aria-live` region (UX spec §2.4/§8, ADR-0064 D1): ONE
       * region per panel, not one per row — avoids a live-region storm on a
       * multi-row failure. Announces each row's add outcome as it resolves.
       * UID-294 (ADR-0104 D3): this region is the reason the visible
       * "Quote accepted…"/"All N in cart" banners above could be removed
       * without losing the add-outcome announcement — it never depended on
       * them. */}
      <p aria-live="polite" className="sr-only rfq-quote-live-region">
        {liveMessage}
      </p>
    </div>
  )
}

/**
 * The row's "More actions" kebab control and its menu.
 *
 * UID-289 (ADR-0064 amendment) added the trigger — matching the design's
 * vertical 3-dot glyph (`Fictiv Platform.dc.html:3585`: 32×32, `<circle
 * cx="12" cy="5|12|19" r="1">`, stroke-width 2.4) — but with no menu behind
 * it: the click surfaced a demo-honest "not yet available" toast, because no
 * per-row action had a supporting endpoint on an already-quoted line.
 *
 * UID-325 (F1) gives it the design's real menu (`dc.html:3588-3592`): a 200px
 * panel holding "Move to a new RFQ" and a red "Remove from request", both
 * 13.5px/600. Built on the SHARED `useRowActionsMenu`/`RowActionsMenuPortal`
 * primitive that UID-308 extracted for `OrderRowMenu`/`RfqRowMenu`, so
 * open/close, outside-pointerdown and Escape dismissal, reflow handling, and
 * the portaled overflow-safe positioning are inherited rather than
 * re-implemented — this parts table is inside a card that clips an in-flow
 * absolute panel exactly the way the recent-orders card did.
 *
 * NOT the same thing as `RfqRowMenu`: that is the Home/Open-quotes menu for a
 * whole RFQ (View / Share / Delete, ADR-0095), a different surface with
 * different items. The ticket read the two as one component and described F1
 * as "an unwired render site"; it is a new menu.
 *
 * UID-611 (ADR-0178 D1/D3, ADR-0179 D1) retires "Remove from request"'s
 * demo-honesty: it now calls the real `DELETE /rfq/{rfqId}/lines/{lineId}`
 * (via the caller's `onRemove`), for every row this menu renders on — not
 * just quoted ones any more (ADR-0178 D5 extends the kebab itself to every
 * status/row). No confirmation dialog, by product decision (ADR-0178 D3):
 * the delete just happens on click, same as the cart's own row menu.
 *
 * "Move to a new RFQ" stays EXACTLY as ADR-0104 D2 shipped it —
 * DEMO-HONEST, no mutation (ADR-0179 D1 leaves this ruling untouched: no
 * bulk/row-level move endpoint was authorized by either ADR). It opens the
 * real `MoveToNewRfqConfirmModal` — the same confirm `RfqReviewPage` uses,
 * where the action IS real because those lines are still a local draft —
 * and confirming here reports honestly that nothing moved.
 *
 * The design's `p.canSelect` gate is mirrored by the caller, which only
 * renders this for a selectable row.
 */
function MoreActionsButton({
  partName,
  onRemove,
  busy = false,
}: {
  readonly partName: string
  /** UID-611 (ADR-0178 D1/D3) — the caller's real `DELETE` (or batch-remove
   * fallthrough, on the bulk bar — this button is per-row only) for this
   * line, on every status. */
  readonly onRemove: () => void
  /** True while this row's DELETE is in flight — disables the menu item so
   * a second click can't race the first. */
  readonly busy?: boolean
}) {
  const { showToast } = useToast()
  const [moveConfirmOpen, setMoveConfirmOpen] = useState(false)
  const { open, coords, containerRef, triggerRef, panelRef, menuId, toggleOpen, runAndClose } =
    useRowActionsMenu()

  /**
   * UID-611 review round (WCAG 2.1.1/4.1.2, finding 4) — this menu's panel is
   * PORTALED to `<body>` (`RowActionsMenuPortal`, `rowActionsMenu.tsx`'s own
   * doc comment), which takes it out of the trigger's DOM subtree. A React
   * portal only affects event bubbling, never layout/tab order — so native
   * Tab from the trigger did NOT reach the portaled items at all; the two
   * `<button>`s inside were only reachable by tabbing through the REST of
   * the document up to wherever `<body>`'s last child happens to sit. A
   * mouse/pointer user could still open and click the menu, so this was
   * invisible without an actual keyboard-only pass — exactly the class of
   * defect an axe-core/DOM-structure check alone would not catch.
   *
   * Fixed the SAME structural remedy this codebase already applied once for
   * this exact defect (`PartsTable.tsx`'s `PartRowActions`, UID-421): move
   * focus onto the FIRST item the moment the menu opens (so Tab/Shift+Tab is
   * never needed to reach it), and rove Up/Down/Home/End between items with
   * the shared hook's Escape-closes/outside-pointerdown-closes behavior
   * untouched. Deliberately KEEPS these two items as plain, roleless
   * `<button>`s (not `role="menuitem"`/a `role="menu"` container, unlike
   * `PartRowActions`) — this popover matches the sibling `OrderRowMenu`/
   * `RfqRowMenu`'s existing "roleless button popover" pattern (this hook's
   * OTHER callers), and every one of this test suite's existing
   * `getByRole('button', { name: 'Remove from request' })`-style queries
   * depends on that implicit role surviving. A `role="menuitem"` button is
   * no longer exposed with role `button` to assistive tech OR testing
   * queries (an explicit ARIA role always wins over the implicit one) — the
   * keyboard-OPERABILITY fix below does not require that role change. */
  useEffect(() => {
    if (!open) return
    panelRef.current?.querySelector<HTMLButtonElement>('.rfq-row-menu-item')?.focus()
  }, [open, panelRef])

  function handleMenuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    const items = Array.from(
      panelRef.current?.querySelectorAll<HTMLButtonElement>('.rfq-row-menu-item') ?? [],
    )
    if (items.length === 0) return
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement)

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      items[(currentIndex + 1) % items.length]?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      items[(currentIndex - 1 + items.length) % items.length]?.focus()
    } else if (event.key === 'Home') {
      event.preventDefault()
      items[0]?.focus()
    } else if (event.key === 'End') {
      event.preventDefault()
      items[items.length - 1]?.focus()
    }
  }

  /**
   * UID-325 (Gate 5, WCAG 2.4.3 Focus Order) — closing the confirm modal must
   * hand focus back to this row's kebab trigger EXPLICITLY.
   *
   * `Modal.tsx` normally restores focus to whatever was focused when it opened,
   * but that fails on this path: `runAndClose` closes the menu and opens the
   * modal in one batch, so the focused menu ITEM is already detached from the
   * document by the time `Modal`'s effect samples `document.activeElement` —
   * it captures `<body>`. `Modal`'s detached-node fallback then can't help
   * either, because its guard (`document.body.contains(previous)`) is trivially
   * true for `body` itself, so `previous.focus()` is a silent no-op and focus
   * is stranded at the top of the document.
   *
   * Neither `OrderRowMenu` nor `RfqRowMenu` opens a modal from a menu item, so
   * this is the first caller to hit it — fixed here rather than in `Modal`,
   * whose "restore what was focused" contract is right for every other caller.
   */
  function closeMoveConfirm() {
    setMoveConfirmOpen(false)
    triggerRef.current?.focus()
  }

  return (
    <div className="row-actions-menu" ref={containerRef}>
      <Button
        type="button"
        variant="outline"
        ref={triggerRef}
        className="rfq-row-more-actions-btn"
        aria-label={`More actions for ${partName}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={toggleOpen}
      >
        <MoreActionsGlyph />
      </Button>
      <RowActionsMenuPortal
        open={open}
        coords={coords}
        menuId={menuId}
        panelRef={panelRef}
        onKeyDown={handleMenuKeyDown}
      >
        <button
          type="button"
          className="rfq-row-menu-item"
          onClick={runAndClose(() => setMoveConfirmOpen(true))}
        >
          {/* The design's glyph is an arrow-out-of-box (`dc.html:3590`).
           * `ExportOutlined` is not in this app's curated `icons.tsx` re-export
           * and `icon-registry.ts` has no equivalent either, so
           * `ShareAltOutlined` — already used by the sibling `RfqRowMenu` for
           * its own "send this elsewhere" item — carries the same meaning
           * without widening either shared registry for one menu row. */}
          <ShareAltOutlined aria-hidden="true" />
          Move to a new RFQ
        </button>
        <button
          type="button"
          className="rfq-row-menu-item row-actions-menu-item-danger"
          disabled={busy}
          onClick={runAndClose(() => onRemove())}
        >
          <DeleteOutlined aria-hidden="true" />
          Remove from request
        </button>
      </RowActionsMenuPortal>
      <MoveToNewRfqConfirmModal
        open={moveConfirmOpen}
        partName={partName}
        // UID-325 (Gate 5) — caller-specific copy. The default asserts the move
        // WILL happen, which is true on `RfqReviewPage` (local draft) but not
        // here: nothing moves a line out of a submitted RFQ. Promising it and
        // then toasting "nothing was moved" would put the falsehood in the
        // PRE-action copy, which is what ADR-0104 D2's demo-honest posture
        // exists to prevent.
        body={
          <>
            Moving <strong>&ldquo;{partName}&rdquo;</strong> out of a submitted request isn&rsquo;t
            available yet — this is a demo, and nothing will be moved.
          </>
        }
        onCancel={closeMoveConfirm}
        onConfirm={() => {
          closeMoveConfirm()
          showToast(
            `Moving “${partName}” out of a submitted RFQ is a demo — nothing was moved.`,
            'info',
          )
        }}
      />
    </div>
  )
}

function MoreActionsGlyph() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '1em',
        height: '1em',
        fontSize: '1em',
        lineHeight: 0,
        flexShrink: 0,
      }}
    >
      <svg
        width="1em"
        height="1em"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="5" r="1" />
        <circle cx="12" cy="12" r="1" />
        <circle cx="12" cy="19" r="1" />
      </svg>
    </span>
  )
}

/**
 * UID-196 (UX spec §2.2/§2.3, ADR-0064 D1/D4) — the 34×34 circular per-row
 * add-to-cart button, all 5 states. Never color-only: every state pairs a
 * distinct icon (or spinner) with an `aria-label` change (UX §2.3/§8).
 *
 * Color: still renders on the `purple` `Button` variant for its base
 * `.btn`/structural classes (unchanged — this is the shared "quote/cart
 * action" variant used elsewhere too, per ADR-0064 D4), but the visible
 * fill/text/border are fully repainted by this component's own scoped
 * `.rfq-row-add-btn` override in rfq.css.
 *
 * UID-238 item 7 originally repainted this teal, superseding ADR-0064 D4's
 * coherence call ("purple is already the quote/cart action color on this
 * surface") in favor of design-fidelity — but it shipped the WRONG color:
 * its audit matched the design source's CSS variable NAME
 * (`--fictiv-teal-*`, Fictiv Platform.dc.html:3442) without checking what
 * that variable actually resolves to. A live browser check
 * (getComputedStyle) showed the design's real rendered color is indigo/
 * navy, not teal. UID-242 item 3 reverts the fill/text/border to this
 * repo's `--color-brand-tint-50`/`--color-brand-primary`/
 * `--color-brand-tint-100` tokens — see rfq.css's `.rfq-row-add-btn`
 * comment for the full rationale and the WCAG 1.4.3 contrast figure. Every
 * OTHER `variant="purple"` consumer in the app remains untouched — D4's
 * coherence call still stands everywhere else; only this one component's
 * scoped override changes.
 */
function RowAddToCartButton({
  partName,
  status,
  errorMessageId,
  onAdd,
}: {
  readonly partName: string
  readonly status: RowAddStatus
  readonly errorMessageId: string
  readonly onAdd: () => void
}) {
  const label =
    status === 'in_flight'
      ? `Adding ${partName} to cart…`
      : status === 'added'
        ? `${partName} is in your cart`
        : status === 'failed'
          ? `Retry adding ${partName} to cart — add failed`
          : `Add ${partName} to cart`

  return (
    <Button
      type="button"
      variant="purple"
      className={`rfq-row-add-btn rfq-row-add-btn--${status}`}
      aria-label={label}
      // 'added' stays a real, focusable status element (never fully removed
      // from the tab order) rather than a native `disabled` — UX spec §2.3.
      aria-disabled={status === 'added' ? true : undefined}
      aria-describedby={status === 'failed' ? errorMessageId : undefined}
      loading={status === 'in_flight'}
      disabled={status === 'in_flight'}
      onClick={onAdd}
    >
      {status === 'idle' && <Icon name="plus" />}
      {status === 'added' && <Icon name="check" />}
      {status === 'failed' && <RetryGlyph />}
    </Button>
  )
}

/**
 * Gate 5 (PR #164) finding #3: the failed state's icon per UX spec §2.3 is a
 * retry / curved-arrow pictogram (`exclamation-circle`, used before this
 * fix, doesn't match the spec — label + color already carry the "failed"
 * meaning, so this is a spec-match fix, not a WCAG one). No matching glyph
 * exists in the shared `icon-registry.ts`, and this fix's scope fence is
 * `RfqCard.tsx`/`RfqCard.test.tsx`/`rfq.css` only — so rather than register a
 * new shared icon name, this mirrors `Icon.tsx`'s exact render contract
 * inline (same `lucide-glyphs.tsx` stroke-icon convention — `viewBox 0 0 24
 * 24`, `stroke="currentColor"`, round caps/joins — as every other icon in
 * this button) so it drops in visually identical to `plus`/`check` above.
 */
function RetryGlyph() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '1em',
        height: '1em',
        fontSize: '1em',
        lineHeight: 0,
        flexShrink: 0,
      }}
    >
      <svg
        width="1em"
        height="1em"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline points="1 4 1 10 7 10" />
        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
      </svg>
    </span>
  )
}

/**
 * UID-586 — the design's per-row `p.isPending` pill (`dc.html:4224`): a
 * spinner (`.fx-spin`) followed by "Quote in progress", in the Unit price
 * column while a leaf awaits the automated quote. Scoped by the caller
 * (`showQuoteInProgressPill`, above) to the Create-RFQ compose flow's own
 * transition only — this component itself renders unconditionally
 * whenever mounted, exactly like `RequirementsCell` below.
 */
function QuoteInProgressPill() {
  return (
    <span className="rfq-quote-in-progress-pill">
      <Spinner size="sm" aria-hidden="true" className="rfq-quote-in-progress-spinner" />
      Quote in progress
    </span>
  )
}

/**
 * UID-171 PART 2 (ADR-0063 §2.3), AMENDED by ADR-0115 D3 — the Requirements
 * column's per-row cell. ALWAYS renders, but its content changed: it used
 * to show the free-text `requirements` snapshot (or a permanent em dash for
 * a line with none). The design's Requirements column (`dc.html:3574-3576`)
 * is a DIFFERENT field entirely — `process` (bold) plus an optional
 * `· material` suffix, falling back to "To quote" (`procLabel: p.process ||
 * 'To quote'`, `:9857`) — **never a dash**.
 *
 * `process` is `RfqLinePartView.part.process` (ADR-0093 §C2); `material`
 * comes from the ONE named `materialLabelForLine` accessor in `domain.ts`
 * (ADR-0111 D7's bag-normalizer rule over `SpecSelection.attributes`) —
 * never an inline key lookup at this render site. React-escaped strict-text
 * render throughout (both are genuinely user/client-authored, the same
 * ADR-0063 render-sink posture); never `dangerouslySetInnerHTML`.
 *
 * The free-text `requirements` snapshot is DROPPED from this surface (it
 * simply stops rendering here) — re-homing it as a per-row tooltip/expander
 * is UID-350 Item 2, not this ticket (ADR-0115 D3 point 3).
 *
 * Gate 5 W2 fix — `process` is a `string | null` wire field with no
 * `minLength`, so an empty or whitespace-only value is possible even though
 * unseeded today. (UID-439 made `process` REAL — it is now derived server-side
 * from the line's persisted `specSelection.specification.process` rather than
 * picked from a demo pool — so a blank value remains possible but never a
 * fabricated one.) `process ?? 'To quote'` only replaced `null`, so `''`/`'   '` fell
 * through as an empty `<span>` — exactly the empty-cell outcome D3/ADR-0063
 * §5 both rule out, and it orphaned a leading `· {material}` when a
 * material was also present. The design's own fallback is FALSY, not
 * nullish (`procLabel: p.process || 'To quote'`, `dc.html:9857`) —
 * `process?.trim() || 'To quote'` matches that, and now agrees with
 * `materialLabelForLine`'s own `trim()`-and-treat-blank-as-absent rule for
 * the same input class. */
function RequirementsCell({
  process,
  material,
}: {
  readonly process: string | null
  readonly material: string | null
}) {
  const processLabel = process?.trim() || 'To quote'
  return (
    <span className="rfq-requirements-value">
      <span className="rfq-requirements-process">{processLabel}</span>
      {material && <span className="rfq-requirements-material"> · {material}</span>}
    </span>
  )
}

// UID-238 item 3: the "Re-request quote" body action (`ReRequestAction`) and
// its `stripLineDecisionFields` helper were removed from here — see the
// removal note above the (now-empty) `expired` render slot in `RfqCard`.
// `RfqDetailSummaryRail.tsx`'s `ExpiredSummaryRail` ("Resubmit RFQ") is the
// sole surviving CTA and already owns an equivalent create-then-submit flow
// with its own local `stripLineDecisionFields` copy.
